import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { format, subMonths, parseISO, formatISO } from "date-fns";
import { toDate } from "date-fns-tz";
import ReactDOM from "react-dom";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useDispatch } from "react-redux";
import { API } from "../utils/apiUrls";
import { fetchWalletBalance } from "../redux/slices/walletSlice";
import { OrderBusinessStatusEnum, getOrderStatusName } from "../utils/enums";
import { formatInIST } from "../utils/dateHelpers";
import { formatCarrierName, formatErrorMessage, getErrorCategory } from "../utils/carrierUtils";
import CustomDateRangePicker from "../utils/CustomDateRangePicker";
import {
  SigmaIcon,
  ExclaimIcon,
  PickupIcon,
  TruckIcon,
  CheckIcon,
  ReverseIcon,
  ConfirmationIcon,
  UnprocessedIcon,
} from "../icons/icon.jsx";

import ClaimUploadModal from "../components/ClaimUploadModal.jsx";
import CreateTicketModal from "../components/support/CreateTicketModal.jsx";
import ticketService from "../services/ticketService.js";
import { downloadShipmentInvoice } from "../services/shipmentInvoiceService.js";
import { downloadRemoteFile, openInNewTab } from "../utils/download.js";
import useAuth from "../hooks/useAuth.js";

const CSVIcon = {
  Download: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 15V3" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Search: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="11" cy="11" r="8" strokeWidth="2" />
      <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  ChevronUp: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 15l-6-6-6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevronDown: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  GripVertical: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="5" r="1" fill="currentColor" />
      <circle cx="9" cy="19" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="5" r="1" fill="currentColor" />
      <circle cx="15" cy="19" r="1" fill="currentColor" />
    </svg>
  ),
  Info: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="8" r="0.5" fill="currentColor" strokeWidth="1" />
    </svg>
  ),
  Check: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Eye: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" />
      <circle cx="12" cy="12" r="3" strokeWidth="2" />
    </svg>
  ),
};

const TABS = [
  { key: "all", label: "Orders", Icon: SigmaIcon, badgeColor: "bg-slate-600", color: "text-slate-600" },
  { key: "pickupPending", label: "Pickups", Icon: PickupIcon, badgeColor: "bg-amber-600", color: "text-amber-600" },
  { key: "inTransit", label: "Transit", Icon: TruckIcon, badgeColor: "bg-yellow-600", color: "text-yellow-700" },
  { key: "outForDelivery", label: "OFD", Icon: TruckIcon, badgeColor: "bg-indigo-600", color: "text-indigo-600" },
  { key: "delivered", label: "Delivered", Icon: CheckIcon, badgeColor: "bg-emerald-600", color: "text-emerald-600" },
  { key: "failed", label: "Failed", Icon: ExclaimIcon, badgeColor: "bg-rose-600", color: "text-rose-600" },
  { key: "rto", label: "RTO", Icon: ExclaimIcon, badgeColor: "bg-orange-600", color: "text-orange-600" },
  { key: "lostDamaged", label: "Lost/Damaged", Icon: ExclaimIcon, badgeColor: "bg-red-600", color: "text-red-600" },
  { key: "rvp", label: "RVP", Icon: ReverseIcon, badgeColor: "bg-purple-600", color: "text-purple-600" },
  { key: "confirmation", label: "Confirmation", Icon: ConfirmationIcon, badgeColor: "bg-cyan-600", color: "text-cyan-600" },
  { key: "unprocessed", label: "Error", Icon: UnprocessedIcon, badgeColor: "bg-pink-600", color: "text-pink-600" },
];

const COURIER_OPTIONS = [
  { value: "DELHIVERY", label: "Delhivery" },
  { value: "BLUEDART", label: "Bluedart" },
  { value: "DTDC", label: "DTDC" },
  { value: "SHADOWFAX", label: "Shadowfax" },
  { value: "XPRESSBEES", label: "XpressBees" },
  { value: "EKART", label: "Ekart" },
  { value: "AMAZON", label: "Amazon" },
  { value: "ATS", label: "Amazon ATS" },
];

const lottieSrc = "/animations/loader.lottie";

const STATUS_MAP = {
  all: [],
  failed: [OrderBusinessStatusEnum.FAILED_DELIVERY.code],
  pickupPending: [
    OrderBusinessStatusEnum.PICKUP_PENDING.code,
    OrderBusinessStatusEnum.OUT_FOR_PICKUP.code,
    OrderBusinessStatusEnum.PICKUP_FAILED.code,
    OrderBusinessStatusEnum.ORDER_PLACED.code,
    OrderBusinessStatusEnum.AWB_REGISTERED.code,
  ],
  inTransit: [
    OrderBusinessStatusEnum.INTRANSIT.code,
    OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
    OrderBusinessStatusEnum.SHIPMENT_HELD.code,
    OrderBusinessStatusEnum.PICKED_UP.code,
  ],
  delivered: [OrderBusinessStatusEnum.DELIVERED.code],
  rto: [
    OrderBusinessStatusEnum.RTO_REQUESTED.code,
    OrderBusinessStatusEnum.RTO.code,
    OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code,
    OrderBusinessStatusEnum.RTO_DELIVERED.code,
    OrderBusinessStatusEnum.RTO_FAILED.code,
    OrderBusinessStatusEnum.RTO_INTRANSIT.code,
    OrderBusinessStatusEnum.RTO_CONTACT_CUSTOMER_CARE.code,
    OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  ],
  outForDelivery: [OrderBusinessStatusEnum.OUT_FOR_DELIVERY.code],
  lostDamaged: [
    OrderBusinessStatusEnum.LOST.code,
    OrderBusinessStatusEnum.DAMAGED.code,
  ],
  unprocessed: [],
  rvp: [],
  confirmation: [],
};

const ConfirmModal = ({ open, title, message, onConfirm, onCancel }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-600 mb-6 leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onCancel();
            }}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all shadow-lg"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : "bg-blue-500";

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-2xl z-50 animate-slide-in-right flex items-center gap-3`}>
      {type === "success" && (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )}
      {type === "error" && (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      )}
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-4 hover:bg-white/20 rounded p-1">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

const StatusDropdown = ({ value, onChange, statuses }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getStatusColor = (code) => {
    const codeStr = String(code);
    if (codeStr.includes('DELIVERED') && !codeStr.includes('FAILED'))
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', icon: 'text-emerald-500' };
    if (codeStr.includes('FAILED'))
      return { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200', icon: 'text-rose-500' };
    if (codeStr.includes('PICKUP') || codeStr.includes('PLACED'))
      return { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200', icon: 'text-amber-500' };
    if (codeStr.includes('TRANSIT') || codeStr.includes('HELD'))
      return { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-200', icon: 'text-blue-500' };
    if (codeStr.includes('RTO'))
      return { bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200', icon: 'text-orange-500' };
    if (codeStr.includes('OUT_FOR_DELIVERY'))
      return { bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-200', icon: 'text-indigo-500' };
    if (codeStr.includes('LOST') || codeStr.includes('DAMAGED'))
      return { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200', icon: 'text-red-500' };
    if (codeStr.includes('CANCELLED'))
      return { bg: 'bg-gray-50', text: 'text-gray-700', ring: 'ring-gray-200', icon: 'text-gray-500' };
    return { bg: 'bg-slate-50', text: 'text-slate-700', ring: 'ring-slate-200', icon: 'text-slate-500' };
  };

  const getStatusIcon = (code) => {
    const codeStr = String(code);
    if (codeStr.includes('DELIVERED') && !codeStr.includes('FAILED'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
    if (codeStr.includes('FAILED'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />;
    if (codeStr.includes('PICKUP'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />;
    if (codeStr.includes('TRANSIT'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />;
    if (codeStr.includes('RTO'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />;
    if (codeStr.includes('OUT_FOR_DELIVERY'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />;
    if (codeStr.includes('CANCELLED'))
      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />;
    return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;
  };

  const filteredStatuses = statuses.filter(status =>
    status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedStatus = statuses.find(s => s.code === value);
  const selectedColor = selectedStatus ? getStatusColor(selectedStatus.code) : null;

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-2.5 py-1.5 text-xs border-2 rounded-lg transition-all bg-white shadow-sm flex items-center justify-between gap-1.5 group ${isOpen
          ? 'ring-2 ring-blue-500 border-blue-500'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
          }`}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {selectedStatus ? (
            <>
              <svg className={`w-4 h-4 flex-shrink-0 ${selectedColor.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {getStatusIcon(selectedStatus.code)}
              </svg>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ring-1 ${selectedColor.bg} ${selectedColor.text} ${selectedColor.ring} truncate`}>
                {selectedStatus.name}
              </span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span className="text-gray-500 font-medium text-xs">All Statuses</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedStatus && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setIsOpen(false);
              }}
              className="p-0.5 hover:bg-gray-100 rounded transition-colors"
              title="Clear selection"
            >
              <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-[9999] mt-2 w-full bg-white rounded-xl shadow-2xl border-2 border-gray-200 overflow-hidden animate-scale-in">
          <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search status..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[320px] overflow-y-auto custom-scrollbar">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-all border-b border-gray-100 ${!value
                ? 'bg-blue-50 text-blue-700'
                : 'hover:bg-gray-50 text-gray-700'
                }`}
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${!value ? 'text-blue-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="font-semibold text-sm">All Statuses</span>
              {!value && (
                <svg className="w-5 h-5 ml-auto text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {filteredStatuses.length > 0 ? (
              filteredStatuses.map((status) => {
                const colors = getStatusColor(status.code);
                const isSelected = value === status.code;
                return (
                  <button
                    key={status.code}
                    type="button"
                    onClick={() => {
                      onChange(status.code);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${isSelected
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                      }`}
                  >
                    <svg className={`w-5 h-5 flex-shrink-0 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {getStatusIcon(status.code)}
                    </svg>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ring-1 ${colors.bg} ${colors.text} ${colors.ring} flex-1 text-left`}>
                      {status.name}
                    </span>
                    {isSelected && (
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-500 font-medium">No statuses found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ tab, onReset }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-32 h-32 mb-6 opacity-30">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-full h-full text-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
    </div>
    <h3 className="text-lg font-bold text-gray-700 mb-2">No shipments found</h3>
    <p className="text-gray-500 mb-6 text-center max-w-md">
      {tab === "all"
        ? "No shipments match your current filters. Try adjusting your search criteria."
        : `No shipments found in the "${TABS.find(t => t.key === tab)?.label}" category.`}
    </p>
    <button
      onClick={onReset}
      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
    >
      Reset Filters
    </button>
  </div>
);

const TableSkeleton = () => (
  <div className="space-y-3 p-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="animate-pulse flex space-x-4 bg-gray-50 p-4 rounded-lg">
        <div className="h-4 w-4 bg-gray-300 rounded"></div>
        <div className="flex-1 space-y-3">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="grid grid-cols-5 gap-4">
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ActionMenu = ({ shipment, onAction, currentPage, onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const isCancelled =
    String(shipment.latest_order_status_code) ===
    OrderBusinessStatusEnum.CANCELLED_ORDER.code;

  const isPickupPending = STATUS_MAP.pickupPending.includes(
    String(shipment.latest_order_status_code)
  );

  const isConfirmationAwaited =
    String(shipment.latest_order_status_code) ===
    OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code;

  const actions = [
    ...(isConfirmationAwaited ? [{
      label: "Place Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      color: "text-cyan-600 hover:bg-cyan-50",
      action: "place_order",
    }] : []),
    {
      label: "View Details",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943
               9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      color: "text-slate-600 hover:bg-slate-50",
      action: "details",
    },
    {
      label: "Download Label",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0
               011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
          />
        </svg>
      ),
      color: isPickupPending ? "text-emerald-600 hover:bg-emerald-50" : "text-gray-400",
      action: "label",
      disabled: !isPickupPending,
    },
    {
      label: "Download Invoice",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      color: shipment.order_reference_number
        ? "text-amber-700 hover:bg-amber-50"
        : "text-gray-400",
      action: "invoice",
      disabled: !shipment.order_reference_number,
    },
    {
      label: "Add Claim",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0
               2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464
               0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      color: "text-purple-600 hover:bg-purple-50",
      action: "claim",
    },
    {
      label: "QC Image",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7.5A2.5 2.5 0 015.5 5h2.379a1 1 0 00.707-.293l1.414-1.414A1 1 0 0110.707 3h2.586a1 1 0 01.707.293l1.414 1.414a1 1 0 00.707.293H18.5A2.5 2.5 0 0121 7.5v9A2.5 2.5 0 0118.5 19h-13A2.5 2.5 0 013 16.5v-9zM12 16a4 4 0 100-8 4 4 0 000 8z"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-indigo-600 hover:bg-indigo-50" : "text-gray-400",
      action: "download_qc_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Proof of Delivery",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-teal-600 hover:bg-teal-50" : "text-gray-400",
      action: "download_epod_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Sorter Image",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-amber-600 hover:bg-amber-50" : "text-gray-400",
      action: "download_sorter_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Support",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      ),
      color: "text-blue-600 hover:bg-blue-50",
      action: "support",
    },
    {
      label: "Clone Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0
               012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0
               002-2v-8a2 2 0 00-2-2h-8a2 2 0
               00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      color: "text-indigo-600 hover:bg-indigo-50",
      action: "clone",
    },
    {
      label: "Cancel Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
      color: isCancelled ? "text-gray-400" : "text-red-600 hover:bg-red-50",
      action: "cancel",
      disabled: isCancelled,
    },
  ];

  const computePosition = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const menuHeight = menuRef.current.offsetHeight || 260;
    const menuWidth = menuRef.current.offsetWidth || 224;

    const margin = 8;

    let top = rect.bottom + margin;
    let placeAbove = false;

    if (top + menuHeight > viewportHeight - margin) {
      top = rect.top - menuHeight - margin;
      placeAbove = true;
    }

    if (top < margin) top = margin;
    if (top + menuHeight > viewportHeight - margin) {
      top = viewportHeight - menuHeight - margin;
    }

    let left = rect.right - menuWidth;

    if (left < margin) left = margin;
    if (left + menuWidth > viewportWidth - margin) {
      left = viewportWidth - menuWidth - margin;
    }

    setPosition({ top, left });
  }, []);

  const handleToggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    onOpenChange?.(willOpen);
  };

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      computePosition();
    });

    const handleWindowChange = () => computePosition();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeMenu]);

  const menuNode =
    isOpen &&
    ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="fixed z-[9999] w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 animate-scale-in"
        style={{ top: position.top, left: position.left }}
      >
        {actions.map((action, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (!action.disabled) {
                onAction(action.action, shipment, currentPage);
                closeMenu();
              }
            }}
            disabled={action.disabled}
            className={`w-full px-4 py-2.5 flex items-center gap-3 transition-all ${action.color
              } ${action.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
          >
            {action.icon}
            <span className="font-medium">{action.label}</span>
          </button>
        ))}
      </div>,
      document.body
    );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="px-4 py-2 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-lg hover:from-slate-700 hover:to-slate-800 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
      >
        <span>Actions</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10
               10.586l3.293-3.293a1 1 0
               111.414 1.414l-4 4a1 1 0
               01-1.414 0l-4-4a1 1 0
               010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {menuNode}
    </div>
  );
};

const STATUSES_REQUIRING_SUPPORT = [
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.FAILED_DELIVERY.code,
  OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_FAILED.code,
  OrderBusinessStatusEnum.DAMAGED.code,
  OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
  OrderBusinessStatusEnum.LOST.code,
  OrderBusinessStatusEnum.CONTACT_CUSTOMER_CARE.code,
  OrderBusinessStatusEnum.SHIPMENT_HELD.code,
  OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];

const StatusBadge = ({ statusCode, shipment, onSupportClick }) => {
  const statusName = getOrderStatusName(statusCode);
  const statusCodeStr = String(statusCode);
  const needsSupport = STATUSES_REQUIRING_SUPPORT.includes(statusCodeStr);

  const getStatusStyle = (code) => {
    const codeStr = String(code);
    if (codeStr === OrderBusinessStatusEnum.DELIVERED.code)
      return "bg-green-100 text-green-800 ring-green-200";
    if (codeStr === OrderBusinessStatusEnum.FAILED_DELIVERY.code)
      return "bg-red-100 text-red-800 ring-red-200";
    if (codeStr === OrderBusinessStatusEnum.OUT_FOR_DELIVERY.code)
      return "bg-blue-100 text-blue-800 ring-blue-200";
    if (codeStr === OrderBusinessStatusEnum.INTRANSIT.code)
      return "bg-yellow-100 text-yellow-800 ring-yellow-200";
    if (codeStr.includes("RTO"))
      return "bg-orange-100 text-orange-800 ring-orange-200";
    return "bg-gray-100 text-gray-800 ring-gray-200";
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ring-1 ${getStatusStyle(statusCode)}`}>
        {statusName}
      </span>
      {needsSupport && shipment && onSupportClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSupportClick(shipment);
          }}
          className="inline-flex items-center justify-center p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm hover:shadow-md"
          title="Create Support Ticket"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default function AllShipments() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const initialPage = location.state?.page ?? 1;

  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const [useDateFilter, setUseDateFilter] = useState(true);

  const [filters, setFilters] = useState({
    orderReferenceNumber: "",
    awbNumber: "",
    paymentMode: "",
    customerPhone: "",
    itemName: "",
    sku: "",
    courierPartnerName: "",
    orderStatus: "",
    orderType: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });

  const SPECIFIC_FIELDS = [
    "orderReferenceNumber",
    "awbNumber",
    "paymentMode",
    "customerPhone",
    "itemName",
    "sku",
    "courierPartnerName",
  ];

  const hasSpecificFilters = (f = filters) =>
    Boolean(
      f.orderReferenceNumber ||
      f.awbNumber ||
      f.paymentMode ||
      f.customerPhone ||
      f.itemName ||
      f.sku ||
      f.courierPartnerName ||
      f.orderStatus
    );

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [limit, setLimit] = useState(50);
  const [sortField, setSortField] = useState("shipment_order_date_time_utc");
  const [sortDirection, setSortDirection] = useState("DESC");

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [statusCounts, setStatusCounts] = useState(Object.fromEntries(TABS.map(t => [t.key, 0])));
  const [expandedRows, setExpandedRows] = useState({});

  const cachedUnprocessedDataRef = useRef(null);

  const [selectedTab, setSelectedTab] = useState("all");

  const [selectedRows, setSelectedRows] = useState(new Set());
  const headerCheckboxRef = useRef(null);
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false);
  
  const [showCSVColumnModal, setShowCSVColumnModal] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [csvColumnSearch, setCsvColumnSearch] = useState("");
  const [csvColumnCategory, setCsvColumnCategory] = useState("all");
  const [selectedCSVColumns, setSelectedCSVColumns] = useState([]);
  const [csvColumnOrder, setCsvColumnOrder] = useState([]);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [csvExportFormat, setCsvExportFormat] = useState("csv");

  const allCSVColumns = [
    { key: "order_reference_number", label: "Order Reference Number", category: "identification", description: "Unique order identifier", defaultSelected: true, icon: "🔢" },
    { key: "awb_number", label: "AWB Number", category: "identification", description: "Airway bill number for tracking", defaultSelected: true, icon: "📦" },
    { key: "marketplace_channel_order_number", label: "Marketplace Channel Order Number", category: "identification", description: "Marketplace/channel order number from search order API", defaultSelected: true, icon: "🛒" },
    { key: "merchant_id", label: "Merchant ID", category: "identification", description: "Merchant identifier", defaultSelected: false, icon: "🏪" },
    { key: "carrier", label: "Carrier", category: "shipping", description: "Shipping carrier name", defaultSelected: true, icon: "🚚" },
    { key: "order_type", label: "Order Type", category: "shipping", description: "Forward or reverse order", defaultSelected: true, icon: "↔️" },
    { key: "latest_order_status", label: "Order Status", category: "shipping", description: "Current shipment status", defaultSelected: true, icon: "📊" },
    { key: "order_placed_date_time", label: "Order Placed Date", category: "shipping", description: "When the order was placed", defaultSelected: true, icon: "📅" },
    { key: "order_label_url", label: "Label URL", category: "shipping", description: "Link to shipping label", defaultSelected: false, icon: "🏷️" },
    { key: "drop_name", label: "Recipient Name", category: "delivery", description: "Delivery recipient name", defaultSelected: true, icon: "👤" },
    { key: "drop_pincode", label: "Pincode", category: "delivery", description: "Delivery pincode", defaultSelected: true, icon: "📍" },
    { key: "drop_city", label: "City", category: "delivery", description: "Delivery city", defaultSelected: true, icon: "🏙️" },
    { key: "drop_state", label: "State", category: "delivery", description: "Delivery state", defaultSelected: true, icon: "🗺️" },
    { key: "payment_mode", label: "Payment Mode", category: "payment", description: "COD, Prepaid, etc.", defaultSelected: true, icon: "💳" },
    { key: "invoice_number", label: "Invoice Number", category: "payment", description: "Invoice reference", defaultSelected: false, icon: "🧾" },
    { key: "invoice_value", label: "Invoice Value", category: "payment", description: "Total invoice amount", defaultSelected: true, icon: "💰" },
    { key: "total_shipping_charge", label: "Shipping Charge", category: "payment", description: "Total shipping cost", defaultSelected: false, icon: "📦" },
    { key: "total_shipping_tax", label: "Shipping Tax", category: "payment", description: "Tax on shipping", defaultSelected: false, icon: "📋" },
    { key: "dead_weight_in_grams", label: "Dead Weight (g)", category: "package", description: "Physical weight in grams", defaultSelected: false, icon: "⚖️" },
    { key: "volumetric_weight_in_grams", label: "Volumetric Weight (g)", category: "package", description: "Calculated volumetric weight", defaultSelected: false, icon: "📏" },
    { key: "item_sku", label: "Item SKU", category: "items", description: "Product SKU codes (semicolon-separated for multiple items)", defaultSelected: true, icon: "🏷️" },
    { key: "item_name", label: "Item Name", category: "items", description: "Product names (semicolon-separated for multiple items)", defaultSelected: true, icon: "📦" },
    { key: "item_quantity", label: "Item Quantity", category: "items", description: "Quantity per item (semicolon-separated, e.g. 1;4;8)", defaultSelected: true, icon: "🔢" },
    { key: "item_unit_price", label: "Unit Price", category: "items", description: "Unit price per item (semicolon-separated)", defaultSelected: true, icon: "💵" },
    { key: "total_quantity", label: "Total Quantity", category: "items", description: "Sum of all item quantities", defaultSelected: true, icon: "📊" },
  ];

  const csvColumnCategories = [
    { key: "all", label: "All Columns", icon: "📋", color: "slate" },
    { key: "identification", label: "Order Identification", icon: "🔢", color: "indigo" },
    { key: "shipping", label: "Shipping Details", icon: "🚚", color: "blue" },
    { key: "delivery", label: "Delivery Address", icon: "📍", color: "emerald" },
    { key: "payment", label: "Payment Information", icon: "💳", color: "amber" },
    { key: "package", label: "Package Details", icon: "📦", color: "purple" },
    { key: "items", label: "Item Details", icon: "🛍️", color: "rose" },
  ];

  useEffect(() => {
    if (selectedCSVColumns.length === 0) {
      const defaultColumns = allCSVColumns
        .filter(col => col.defaultSelected)
        .map(col => col.key);
      setSelectedCSVColumns(defaultColumns);
      setCsvColumnOrder(defaultColumns);
    }
  }, []);

  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimShipment, setClaimShipment] = useState(null);
  const [claimDefaultItem, setClaimDefaultItem] = useState(null);

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportOrderContext, setSupportOrderContext] = useState(null);
  const { getMerchantId } = useAuth();

  const [toast, setToast] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [viewMode, setViewMode] = useState('compact');

  const [openActionMenuRow, setOpenActionMenuRow] = useState(null);

  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const [analyticsDateRange, setAnalyticsDateRange] = useState('last30days');

  const [unprocessedFilters, setUnprocessedFilters] = useState({
    orderReferenceNumber: "",
    pickupPincode: "",
    dropPincode: "",
    allocationStatus: "false",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });
  const [expandedAllocLogs, setExpandedAllocLogs] = useState({});

  const [confirmationFilters, setConfirmationFilters] = useState({
    orderReferenceNumber: "",
    customerPhone: "",
    confirmationStatus: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const openClaimModalFor = (shipment) => {
    const items = (shipment?.shipment_packages ?? []).flatMap(p => p.items ?? []);
    const firstItem = items.length ? items[0] : null;
    setClaimShipment(shipment);
    setClaimDefaultItem(firstItem);
    setClaimModalOpen(true);
  };


  const toggleRow = (idx) => {
    setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  useEffect(() => {
    if (!hasSpecificFilters() && !useDateFilter) {
      setUseDateFilter(true);
      setFilters((prev) => ({
        ...prev,
        fromDate: defaultFrom,
        toDate: defaultTo,
      }));
    }
  }, [
    filters.orderReferenceNumber,
    filters.awbNumber,
    filters.paymentMode,
    filters.customerPhone,
    filters.itemName,
    filters.sku,
    filters.courierPartnerName,
    filters.orderStatus,
    useDateFilter,
  ]);

  const fetchData = useCallback(
    async (tabKey, overrides = {}) => {
      setLoading(true);
      setError(null);
      setShipments([]);
      setTotalRecords(0);
      try {

        const merchantId = localStorage.getItem("merchantId");
        const authToken = localStorage.getItem("authToken");
        if (!authToken) return;

        if (tabKey === "unprocessed") {
          const uf = overrides._unprocessedFilters || unprocessedFilters;
          const fromUTC = uf.fromDate
            ? formatISO(toDate(parseISO(`${uf.fromDate}T00:00:00+05:30`)))
            : null;
          const toUTC = uf.toDate
            ? formatISO(toDate(parseISO(`${uf.toDate}T23:59:59+05:30`)))
            : null;

          const body = {
            merchant_id: merchantId,
            ...(uf.orderReferenceNumber && { order_reference_number: uf.orderReferenceNumber }),
            ...(uf.pickupPincode && { pickup_pincode: uf.pickupPincode }),
            ...(uf.dropPincode && { drop_pincode: uf.dropPincode }),
            is_successful_allocation: uf.allocationStatus !== "" ? uf.allocationStatus === "true" : false,
            ...(fromUTC && { from_date: fromUTC }),
            ...(toUTC && { to_date: toUTC }),
            page: overrides.page || currentPage,
            limit: overrides.limit || limit,
            sort_field: overrides.sort_field || "updated_date_time",
            sort_direction: overrides.sort_direction || sortDirection,
          };

          const res = await axios.post(
            API.searchUnprocessedOrders(),
            body,
            { headers: { Authorization: authToken, "Content-Type": "application/json" } }
          );

          const data = res.data;
          setShipments(Array.isArray(data.orders) ? data.orders : []);
          setTotalRecords(data.total_elements ?? 0);
          return;
        }

        if (tabKey === "confirmation") {
          const cf = overrides._confirmationFilters || confirmationFilters;
          const fromUTC = cf.fromDate
            ? formatISO(toDate(parseISO(`${cf.fromDate}T00:00:00+05:30`)))
            : null;
          const toUTC = cf.toDate
            ? formatISO(toDate(parseISO(`${cf.toDate}T23:59:59+05:30`)))
            : null;

          const body = {
            merchant_id: merchantId,
            ...(cf.orderReferenceNumber && { order_reference_number: cf.orderReferenceNumber }),
            ...(cf.customerPhone && { customer_phone: cf.customerPhone }),
            ...(cf.confirmationStatus && { confirmation_status: [cf.confirmationStatus] }),
            ...(fromUTC && { from_order_creation_datetime_utc: fromUTC }),
            ...(toUTC && { to_order_creation_datetime_utc: toUTC }),
            limit: overrides.limit || limit,
            skip: overrides.skip ?? (currentPage - 1) * limit,
            sort_direction: sortDirection,
          };

          const res = await axios.post(
            API.searchConfirmationOrders(),
            body,
            { headers: { Authorization: authToken, "Content-Type": "application/json" } }
          );

          const data = res.data;
          setShipments(Array.isArray(data.orders) ? data.orders : []);
          setTotalRecords(data.orders_count ?? 0);
          return;
        }

        const fromUTC = useDateFilter
          ? formatISO(toDate(parseISO(`${filters.fromDate}T00:00:00+05:30`)))
          : null;
        const toUTC = useDateFilter
          ? formatISO(toDate(parseISO(`${filters.toDate}T23:59:59+05:30`)))
          : null;

        let statusPayload = STATUS_MAP[tabKey]?.length ? STATUS_MAP[tabKey] : null;
        if (tabKey === "all" && filters.orderStatus) statusPayload = [filters.orderStatus];

        const shipmentOrderType = tabKey === "rvp"
          ? "REVERSE"
          : (filters.orderType || null);

        const body = {
          merchant_id: merchantId,
          order_reference_number: filters.orderReferenceNumber || null,
          awb_number: filters.awbNumber || null,
          payment_mode: filters.paymentMode || null,
          customer_phone: filters.customerPhone || null,
          item_name: filters.itemName || null,
          sku: filters.sku || null,
          courier_partner_name: filters.courierPartnerName || null,
          order_status: statusPayload,
          ...(useDateFilter && fromUTC && {
            from_shipment_order_creation_datetime_utc: fromUTC
          }),
          ...(useDateFilter && toUTC && {
            to_shipment_order_creation_datetime_utc: toUTC
          }),
          limit,
          skip: (currentPage - 1) * limit,
          sort_field: sortField,
          sort_direction: sortDirection,
          ...overrides,
          ...(tabKey === "rvp" && { shipment_order_type: "REVERSE" }),
          ...(tabKey !== "rvp" && !("shipment_order_type" in overrides) && shipmentOrderType && { shipment_order_type: shipmentOrderType }),
        };

        const res = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
          body,
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );

        const data = res.data;
        setShipments(Array.isArray(data.orders) ? data.orders : []);
        setTotalRecords(data.totalCount ?? 0);
      } catch (err) {
        const errMsg = tabKey === "unprocessed" ? "Failed to fetch unprocessed orders."
          : tabKey === "confirmation" ? "Failed to fetch confirmation orders."
          : "Failed to fetch shipments.";
        setError(errMsg);
        showToast(errMsg, "error");
      } finally {
        setLoading(false);
      }
    },
    [filters, currentPage, limit, sortField, sortDirection, useDateFilter, unprocessedFilters, confirmationFilters]
  );

  const fetchStatusCounts = useCallback(async () => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!authToken || !merchantId) return;

      const today = new Date();
      let from, to;
      
      if (analyticsDateRange === 'thisMonth') {
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
      } else {
        from = new Date(today);
        from.setDate(today.getDate() - 30);
        to = today;
      }

      const fromUTC = formatISO(toDate(parseISO(`${from.toLocaleDateString("en-CA")}T00:00:00+05:30`)));
      const toUTC = formatISO(toDate(parseISO(`${to.toLocaleDateString("en-CA")}T23:59:59+05:30`)));

      const body = {
        merchant_id: merchantId,
        from_date_time: fromUTC,
        to_date_time: toUTC,
        include_no_status: false,
      };

      const res = await axios.post(
        API.orderStatusAnalytics(),
        body,
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );

      const statusCountsData = res.data?.status_counts || [];
      const summary = res.data?.summary || {};
      
      const counts = {
        all: summary.total_orders || 0,
        failed: 0,
        unprocessed: 0,
        pickupPending: 0,
        inTransit: 0,
        delivered: 0,
        rto: 0,
        outForDelivery: 0,
        lostDamaged: 0,
        rvp: 0,
        confirmation: 0,
      };

      statusCountsData.forEach(item => {
        const code = String(item.status_code);
        const count = item.count || 0;
        
        if (STATUS_MAP.failed.includes(code)) {
          counts.failed += count;
        }
        if (STATUS_MAP.pickupPending.includes(code)) {
          counts.pickupPending += count;
        }
        if (STATUS_MAP.inTransit.includes(code)) {
          counts.inTransit += count;
        }
        if (STATUS_MAP.delivered.includes(code)) {
          counts.delivered += count;
        }
        if (STATUS_MAP.rto.includes(code)) {
          counts.rto += count;
        }
        if (STATUS_MAP.outForDelivery.includes(code)) {
          counts.outForDelivery += count;
        }
        if (STATUS_MAP.lostDamaged.includes(code)) {
          counts.lostDamaged += count;
        }
        if (code === OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code) {
          counts.confirmation += count;
        }
      });

      try {
        const rvpBody = {
          merchant_id: merchantId,
          shipment_order_type: "REVERSE",
          from_shipment_order_creation_datetime_utc: fromUTC,
          to_shipment_order_creation_datetime_utc: toUTC,
          limit: 1,
          skip: 0,
        };

        const rvpRes = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
          rvpBody,
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );

        counts.rvp = rvpRes.data?.totalCount || 0;
      } catch (rvpErr) {
      }

      try {
        const unprocessedBody = {
          merchant_id: merchantId,
          is_successful_allocation: false,
          from_date: fromUTC,
          to_date: toUTC,
          page: 1,
          limit: limit, // fetch full first page so we can cache it
          sort_field: "updated_date_time",
          sort_direction: "DESC",
        };

        const unprocessedRes = await axios.post(
          API.searchUnprocessedOrders(),
          unprocessedBody,
          { headers: { Authorization: authToken, "Content-Type": "application/json" } }
        );

        counts.unprocessed = unprocessedRes.data?.total_elements || 0;

        cachedUnprocessedDataRef.current = {
          orders: Array.isArray(unprocessedRes.data?.orders) ? unprocessedRes.data.orders : [],
          total_elements: unprocessedRes.data?.total_elements || 0,
          timestamp: Date.now(),
        };
      } catch (unprocessedErr) {
      }

      setStatusCounts(counts);
    } catch (err) {
    }
  }, [analyticsDateRange]);

  useEffect(() => {
    fetchData("all", { skip: (initialPage - 1) * limit });
    fetchStatusCounts();
  }, []);

  useEffect(() => {
    fetchStatusCounts();
  }, [analyticsDateRange, fetchStatusCounts]);

  const handleTabClick = async (key) => {
    setSelectedTab(key);
    setCurrentPage(1);
    setExpandedRows({});
    setExpandedAllocLogs({});
    setSelectedRows(new Set());

    if (key === "unprocessed") {
      const freshFilters = {
        orderReferenceNumber: "",
        pickupPincode: "",
        dropPincode: "",
        allocationStatus: "false",
        fromDate: defaultFrom,
        toDate: defaultTo,
      };
      setUnprocessedFilters(freshFilters);

      const cache = cachedUnprocessedDataRef.current;
      if (cache && (Date.now() - cache.timestamp < 60000)) {
        setShipments(cache.orders);
        setTotalRecords(cache.total_elements);
        setLoading(false);
        cachedUnprocessedDataRef.current = null;
        return;
      }

      await fetchData(key, { page: 1, _unprocessedFilters: freshFilters });
      return;
    }

    if (key === "confirmation") {
      const freshFilters = {
        orderReferenceNumber: "",
        customerPhone: "",
        confirmationStatus: "",
        fromDate: defaultFrom,
        toDate: defaultTo,
      };
      setConfirmationFilters(freshFilters);
      await fetchData(key, { skip: 0, _confirmationFilters: freshFilters });
      return;
    }

    setFilters((prev) => {
      const base = {
        ...prev,
        orderReferenceNumber: "",
        awbNumber: "",
        paymentMode: "",
        customerPhone: "",
        itemName: "",
        sku: "",
        courierPartnerName: "",
        fromDate: defaultFrom,
        toDate: defaultTo,
        orderType: key === "rvp" ? "REVERSE" : "",
      };

      return key !== "all"
        ? { ...base, orderStatus: "" }
        : base;
    });

    setUseDateFilter(true);

    const overrides = {
      skip: 0,
      order_reference_number: null,
      awb_number: null,
      payment_mode: null,
      customer_phone: null,
      item_name: null,
      sku: null,
      courier_partner_name: null,
      ...(key === "all" ? { order_status: null } : {}),
      ...(key !== "rvp" ? { shipment_order_type: null } : {}),
    };

    await fetchData(key, overrides);
  };


  const handleFilterClick = async () => {
    setCurrentPage(1);
    await fetchData(selectedTab, { skip: 0 });
    showToast("Filters applied successfully", "success");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (["fromDate", "toDate"].includes(name)) {
      const from = new Date(name === "fromDate" ? value : filters.fromDate);
      const to = new Date(name === "toDate" ? value : filters.toDate);
      if ((to - from) / (1000 * 60 * 60 * 24) > 30) {
        showToast("Date range cannot exceed 1 month", "error");
        return;
      }
    }
    if (SPECIFIC_FIELDS.includes(name) && value) {
      setUseDateFilter(false);
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      orderReferenceNumber: "",
      awbNumber: "",
      paymentMode: "",
      customerPhone: "",
      itemName: "",
      sku: "",
      courierPartnerName: "",
      orderStatus: "",
      orderType: selectedTab === "rvp" ? "REVERSE" : "",
      fromDate: defaultFrom,
      toDate: defaultTo,
    });
    setUseDateFilter(true);
    setCurrentPage(1);
    showToast("Filters reset", "info");
  };

  const handleUnprocessedInputChange = (e) => {
    const { name, value } = e.target;
    setUnprocessedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleUnprocessedFilterClick = async () => {
    setCurrentPage(1);
    await fetchData("unprocessed", { page: 1 });
    showToast("Filters applied successfully", "success");
  };

  const resetUnprocessedFilters = () => {
    const freshFilters = {
      orderReferenceNumber: "",
      pickupPincode: "",
      dropPincode: "",
      allocationStatus: "false",
      fromDate: defaultFrom,
      toDate: defaultTo,
    };
    setUnprocessedFilters(freshFilters);
    setCurrentPage(1);
    fetchData("unprocessed", { page: 1, _unprocessedFilters: freshFilters });
    showToast("Filters reset", "info");
  };

  const handleConfirmationInputChange = (e) => {
    const { name, value } = e.target;
    setConfirmationFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmationFilterClick = async () => {
    setCurrentPage(1);
    await fetchData("confirmation", { skip: 0 });
    showToast("Filters applied successfully", "success");
  };

  const resetConfirmationFilters = () => {
    const freshFilters = {
      orderReferenceNumber: "",
      customerPhone: "",
      confirmationStatus: "",
      fromDate: defaultFrom,
      toDate: defaultTo,
    };
    setConfirmationFilters(freshFilters);
    setCurrentPage(1);
    fetchData("confirmation", { skip: 0, _confirmationFilters: freshFilters });
    showToast("Filters reset", "info");
  };

  const handleConvertToDraft = async (order) => {
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      if (!authToken) { navigate("/login"); return; }

      const res = await axios.post(
        API.draftOrderFromOrder(order.shipment_order_id),
        {},
        { headers: { Authorization: authToken, "Content-Type": "application/json", merchantId } }
      );

      if (res.data?.metadata?.success) {
        showToast("Order converted to draft successfully!", "success");
        navigate("/draft-orders");
      } else {
        showToast(res.data?.metadata?.message || "Failed to convert to draft", "error");
      }
    } catch (err) {
      showToast(err?.response?.data?.metadata?.message || "Failed to convert to draft", "error");
    }
  };

  const [retryingOrderIds, setRetryingOrderIds] = useState(new Set());
  const handleRetryOrder = async (order) => {
    const orderRef = order.order_reference_number;
    if (!orderRef) { showToast("No order reference number found", "error"); return; }
    setRetryingOrderIds(prev => new Set(prev).add(orderRef));
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      if (!authToken) { navigate("/login"); return; }

      const res = await axios.post(
        API.searchOrders(),
        { merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 },
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );
      const fetched = res.data?.orders?.[0];
      if (!fetched) { showToast("Order not found in search results", "error"); return; }

      if (fetched.order_reference_number) {
        fetched.order_reference_number = fetched.order_reference_number + "_retry";
      }

      const orderType = (fetched.shipment_order_type || "").toUpperCase();
      const isReverse = orderType === "REVERSE" || orderType === "RVP";
      navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: fetched } });
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to fetch order for retry", "error");
    } finally {
      setRetryingOrderIds(prev => { const n = new Set(prev); n.delete(orderRef); return n; });
    }
  };

  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
    fetchData(selectedTab, { sort_field: field, sort_direction: newDirection, skip: 0 });
  };

  const nextPage = () => {
    if (shipments.length === limit) {
      const next = currentPage + 1;
      setCurrentPage(next);
      if (selectedTab === "unprocessed") {
        fetchData(selectedTab, { page: next });
      } else {
        fetchData(selectedTab, { skip: (next - 1) * limit });
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      if (selectedTab === "unprocessed") {
        fetchData(selectedTab, { page: prev });
      } else {
        fetchData(selectedTab, { skip: (prev - 1) * limit });
      }
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = +e.target.value;
    setLimit(newLimit);
    setCurrentPage(1);
    fetchData(selectedTab, { limit: newLimit, skip: 0 });
  };

  const downloadLabel = (url) => {
    if (!url) {
      showToast("No label available for this order", "error");
      return;
    }
    window.open(url, "_blank");
    showToast("Label downloaded successfully", "success");
  };

  const handleCancelOrder = async (waybill) => {

    if (!waybill) {
      showToast("No AWB to cancel", "error");
      return;
    }

    const authToken = localStorage.getItem("authToken");
    if (!authToken) return navigate("/login");

    try {
      await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CANCEL_ORDER_URL}`,
        { waybill },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );

      dispatch(fetchWalletBalance());
      new BroadcastChannel('wallet').postMessage('refresh-balance');
      await fetchData(selectedTab, { skip: (currentPage - 1) * limit });
      showToast("Order cancelled successfully", "success");
    } catch (err) {
      showToast("Failed to cancel order", "error");
    }
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const awbMouseDownRef = useRef({ x: 0, y: 0, time: 0 });

  const handleAwbMouseDown = (e) => {
    awbMouseDownRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };

  const handleAwbMouseUp = (e, awb) => {
    const { x, y, time } = awbMouseDownRef.current;
    const dx = Math.abs(e.clientX - x);
    const dy = Math.abs(e.clientY - y);
    const elapsed = Date.now() - time;
    const hasTextSelection = window.getSelection()?.toString().length > 0;
    if (dx < 5 && dy < 5 && elapsed < 300 && !hasTextSelection) {
      const url = `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(awb)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      showToast("Failed to copy", "error");
    }
  };

  const rowKey = (s) => (s.awb_number ? String(s.awb_number) : `REF:${String(s.order_reference_number)}`);
  const isSelectedOnPage = (s) => selectedRows.has(rowKey(s));
  const pageSelectedCount = shipments.filter(isSelectedOnPage).length;
  const allOnPageSelected = shipments.length > 0 && shipments.every(isSelectedOnPage);
  const showMobileActions = pageSelectedCount > 0 || loadingDownloadSelected;

  const toggleSelectOne = (key) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (checked) shipments.forEach(s => next.add(rowKey(s)));
      else shipments.forEach(s => next.delete(rowKey(s)));
      return next;
    });
  };

  const clearSelection = () => setSelectedRows(new Set());

  useEffect(() => {
    const cb = headerCheckboxRef.current;
    if (!cb) return;
    cb.indeterminate = pageSelectedCount > 0 && !allOnPageSelected;
  }, [pageSelectedCount, allOnPageSelected, shipments]);

  const downloadShipmentLabelsServer = async (orderRefs) => {
    const authToken = localStorage.getItem('authToken');
    const merchantId = localStorage.getItem('merchantId');
    const OMS_BASE = process.env.REACT_APP_OMS_BASE_URL;
    if (!authToken || !merchantId || !OMS_BASE) {
      throw new Error("Missing config/env for bulk label API");
    }
    const apiUrl = `${OMS_BASE}/v2/oms/shipment-label/bulk`;
    const payload = {
      merchant_id: merchantId,
      order_reference_numbers: orderRefs,
    };
    const { data } = await axios.post(apiUrl, payload, {
      headers: { Authorization: authToken, "Content-Type": "application/json" },
    });

    const mergedUrl = data?.merged_shipment_label_presigned_url;
    const summary = data?.summary || {};
    const successCount = Number(summary.success_count) || 0;
    const failedCount = Number(summary.failed_count) || 0;
    const totalCount = Number(summary.total) || orderRefs.length;

    if (!mergedUrl) {
      const failedRefs = Array.isArray(data?.results)
        ? data.results.filter(r => r && r.success === false).map(r => r.order_reference_number)
        : [];
      const detail = failedRefs.length > 0 ? ` Failed: ${failedRefs.join(", ")}` : "";
      throw new Error(`No merged shipment label URL returned by server.${detail}`);
    }

    openInNewTab(mergedUrl);

    return { mergedUrl, summary: { total: totalCount, success_count: successCount, failed_count: failedCount } };
  };

  const handleDownloadSelectedLabels = async () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    const orderRefs = selectedOnPage.map(s => s.order_reference_number).filter(Boolean);

    if (orderRefs.length === 0) {
      showToast("No order references in selected rows", "error");
      return;
    }

    setLoadingDownloadSelected(true);
    try {
      const result = await downloadShipmentLabelsServer(orderRefs);
      const { success_count: successCount = orderRefs.length, failed_count: failedCount = 0 } = result?.summary || {};
      if (failedCount > 0) {
        showToast(
          `${successCount} label(s) downloaded, ${failedCount} failed`,
          "info"
        );
      } else {
        showToast(`${successCount} label(s) downloaded successfully`, "success");
      }
    } catch (err) {

      const withLabels = selectedOnPage.filter(s => !!s.order_label_url);
      if (withLabels.length === 0) {
        showToast("Selected rows have no label URLs yet", "error");
      } else {
        withLabels.forEach((s, idx) => {
          const a = document.createElement("a");
          a.href = s.order_label_url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.download = `${s.awb_number || s.order_reference_number || "label"}.pdf`;
          document.body.appendChild(a);
          setTimeout(() => {
            a.click();
            document.body.removeChild(a);
          }, idx * 200);
        });
        const skipped = selectedOnPage.length - withLabels.length;
        setTimeout(() => {
          showToast(
            skipped > 0
              ? `${withLabels.length} labels downloaded, ${skipped} skipped`
              : `${withLabels.length} labels downloaded`,
            skipped > 0 ? "info" : "success"
          );
        }, withLabels.length * 200 + 100);
      }
    } finally {
      setLoadingDownloadSelected(false);
    }
  };

  const handleOpenCSVModal = () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    if (selectedOnPage.length === 0) {
      showToast("No rows selected to export", "error");
      return;
    }
    setShowCSVColumnModal(true);
  };

  const handleDownloadSelectedCSV = () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    if (selectedOnPage.length === 0) {
      showToast("No rows selected to export", "error");
      return;
    }

    const columnsToExport = csvColumnOrder.length > 0 
      ? csvColumnOrder.filter(key => selectedCSVColumns.includes(key))
      : selectedCSVColumns.length > 0
        ? selectedCSVColumns
        : allCSVColumns.filter(col => col.defaultSelected).map(col => col.key);

    // Get column definitions in the correct order
    const orderedColumns = columnsToExport
      .map(key => allCSVColumns.find(col => col.key === key))
      .filter(Boolean);

    const headers = orderedColumns.map(col => col.key);

    const getItemVal = (item, path) => (item && safe(item, path, "")) ?? "";

    const getColumnValue = (shipment, items, columnKey) => {
      const itemVals = (items ?? []).length > 0 ? items : [];
      switch (columnKey) {
        case "merchant_id": return safe(shipment, "merchant_id", "");
        case "order_reference_number": return safe(shipment, "order_reference_number", "");
        case "awb_number": return safe(shipment, "awb_number", "");
        case "marketplace_channel_order_number": return safe(shipment, "marketplace_channel_order_number", "");
        case "carrier": return safe(shipment, "allocated_carrier_partner_public_name", "");
        case "order_type": return safe(shipment, "order_type", "");
        case "payment_mode": return safe(shipment, "payment_details.payment_mode", "") || safe(shipment, "order_type", "");
        case "drop_name": return safe(shipment, "drop_name", "");
        case "drop_pincode": return safe(shipment, "drop_pincode", "");
        case "drop_city": return safe(shipment, "drop_city", "");
        case "drop_state": return safe(shipment, "drop_state", "");
        case "invoice_number": return safe(shipment, "invoice_number", "");
        case "invoice_value": return safe(shipment, "payment_details.total_amount", "") || "";
        case "total_shipping_charge": return safe(shipment, "total_shipping_charge", "");
        case "total_shipping_tax": return safe(shipment, "total_shipping_tax", "");
        case "dead_weight_in_grams": return safe(shipment, "dead_weight_in_grams", "");
        case "volumetric_weight_in_grams": return safe(shipment, "volumetric_weight_in_grams", "");
        case "latest_order_status": return safe(shipment, "latest_order_status", "") || safe(shipment, "latest_order_status_code", "");
        case "order_placed_date_time": return safe(shipment, "order_placed_date_time", "");
        case "order_label_url": return safe(shipment, "order_label_url", "");
        case "item_sku": return itemVals.map(it => getItemVal(it, "item_general_details.sku")).join(";");
        case "item_name": return itemVals.map(it => getItemVal(it, "item_general_details.item_name")).join(";");
        case "item_quantity": return itemVals.map(it => getItemVal(it, "quantity")).join(";");
        case "item_unit_price": return itemVals.map(it => getItemVal(it, "item_payment_details.unit_price")).join(";");
        case "total_quantity": return itemVals.length === 0 ? "" : String(itemVals.reduce((sum, it) => sum + (Number(getItemVal(it, "quantity")) || 0), 0));
        default: return "";
      }
    };

    const rows = [];
    selectedOnPage.forEach((s) => {
      const items = (s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []);
      const row = headers.map(key => getColumnValue(s, items, key));
      rows.push(row);
    });

    const escape = (v) => {
      if (v === undefined || v === null) return '""';
      const str = String(v);
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const csvContent = [
      headers.map(escape).join(","),
      ...rows.map(r => r.map(escape).join(","))
    ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`CSV exported with ${selectedOnPage.length} order(s)`, "success");
  };

  const safe = (obj, path, fallback = "") => {
    try {
      return path.split('.').reduce((a, b) => (a && a[b] !== undefined ? a[b] : undefined), obj) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const handleBypassConfirmation = async (shipment) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      showToast("Session expired. Please log in again", "error");
      navigate("/login");
      return;
    }
    try {
      const res = await axios.post(
        API.bypassOrderConfirmation(),
        { order_reference_number: shipment.order_reference_number },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );
      if (res.data?.metadata?.success) {
        showToast(res.data.metadata.message || "Order placed successfully", "success");
        fetchData(selectedTab, { skip: (currentPage - 1) * limit });
        fetchStatusCounts();
      } else {
        showToast(res.data?.metadata?.message || "Failed to place order", "error");
      }
    } catch (err) {
      const msg = err?.response?.data?.metadata?.message || err?.response?.data?.message || "Failed to place order";
      showToast(msg, "error");
    }
  };

  const handleActionClick = (action, shipment, page) => {
    switch (action) {
      case "place_order":
        setConfirmModal({
          open: true,
          title: "Place Order",
          message: `Are you sure you want to place order ${shipment.order_reference_number}? This will bypass the confirmation step and initiate shipment processing.`,
          onConfirm: () => handleBypassConfirmation(shipment),
        });
        break;
      case "details":
        const authToken = localStorage.getItem("authToken");
        const merchantId = localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          showToast("Session expired. Please log in again", "error");
          navigate("/login");
          return;
        }
        const ref = encodeURIComponent(shipment.order_reference_number);
        const url = `${window.location.origin}/shipment-details/${ref}?page=${page}`;
        window.open(url, "_blank", "noopener,noreferrer");
        break;
      case "label":
        downloadLabel(shipment.order_label_url);
        break;
      case "invoice": {
        const orderRef = shipment.order_reference_number;
        if (!orderRef) {
          showToast("No order reference for this shipment", "error");
          break;
        }
        const authToken = localStorage.getItem("authToken");
        const merchantId = getMerchantId() || localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          showToast("Session expired. Please log in again", "error");
          navigate("/login");
          break;
        }
        (async () => {
          try {
            await downloadShipmentInvoice({
              merchantId,
              orderReferenceNumber: orderRef,
              authToken,
            });
            showToast("Invoice opened in a new tab", "success");
          } catch (err) {
            showToast(err?.message || "Failed to download invoice", "error");
          }
        })();
        break;
      }
      case "claim":
        openClaimModalFor(shipment);
        break;
      case "clone":
        navigate("/create-forward-order", { state: { cloneFrom: shipment } });
        break;
      case "cancel":
        setConfirmModal({
          open: true,
          title: "Cancel Order",
          message: `Are you sure you want to cancel order with AWB ${shipment.awb_number}? This action cannot be undone.`,
          onConfirm: () => handleCancelOrder(shipment.awb_number)
        });
        break;
      case "support":
        handleOpenSupport(shipment);
        break;
      case "download_qc_image":
      case "download_epod_image":
      case "download_sorter_image": {
        const awb = shipment.awb_number;
        if (!awb) {
          showToast("AWB number not available for this shipment", "error");
          break;
        }
        const slugMap = {
          download_qc_image: "qc",
          download_epod_image: "epod",
          download_sorter_image: "sorter",
        };
        const labelMap = {
          download_qc_image: "QC image",
          download_epod_image: "Proof of delivery",
          download_sorter_image: "Sorter image",
        };
        const slug = slugMap[action];
        const friendlyLabel = labelMap[action];
        const authToken = localStorage.getItem("authToken");
        const merchantId = getMerchantId() || localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          showToast("Session expired. Please log in again", "error");
          navigate("/login");
          break;
        }
        (async () => {
          try {
            const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/carrier-images/${slug}?awbNumber=${encodeURIComponent(awb)}`;
            const response = await axios.get(apiUrl, {
              headers: {
                "x-merchant-id": merchantId,
                Authorization: authToken,
                "Content-Type": "application/json",
              },
            });
            const presigned =
              response?.data?.presignedUrlForImageAccess ||
              response?.data?.presigned_url_for_image_access;
            const suggestedName =
              response?.data?.suggestedDownloadFileName ||
              response?.data?.suggested_download_file_name;
            const contentType =
              response?.data?.storedImageContentType ||
              response?.data?.stored_image_content_type ||
              "image/jpeg";
            if (!presigned) {
              showToast(`${friendlyLabel} not yet received from carrier`, "error");
              return;
            }
            const ext = (contentType.split("/")[1] || "jpg").toLowerCase();
            const fallbackName = suggestedName || `${awb}-${slug}.${ext}`;
            await downloadRemoteFile(presigned, fallbackName);
            showToast(`${friendlyLabel} downloaded`, "success");
          } catch (err) {
            if (err && err.name === "DownloadBlockedError") {
              showToast(
                `${friendlyLabel} could not be downloaded directly. Please contact support.`,
                "error",
              );
              return;
            }
            const status = err?.response?.status;
            const data = err?.response?.data || {};
            const code = data.errorCode || data.code;
            const message = data.errorMessage || data.message || err?.message;
            const isNotFound =
              status === 404 ||
              code === "carrier_image_not_found_for_awb" ||
              code === "CARRIER_IMAGE_NOT_FOUND_FOR_AWB" ||
              code === "OMS_CARRIER_IMAGE_NOT_FOUND";
            const friendly = isNotFound
              ? `${friendlyLabel} not yet received from carrier`
              : message || `Could not download ${friendlyLabel}. Please try again.`;
            showToast(friendly, "error");
          }
        })();
        break;
      }
      default:
        break;
    }
  };

  const handleOpenSupport = async (shipment) => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      showToast("Please log in to create a support ticket", "error");
      return;
    }

    const orderRef = shipment.order_reference_number;
    if (!orderRef) {
      showToast("Order reference number not found", "error");
      return;
    }

    try {
      const existingTicket = await ticketService.getTicketByOrder(merchantId, orderRef);
      navigate(`/support/tickets/${existingTicket.public_id}`);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSupportOrderContext({
          orderReferenceNumber: orderRef,
          awbNumber: shipment.awb_number,
        });
        setSupportModalOpen(true);
      } else {
        showToast(err?.response?.data?.message || "Failed to check for existing ticket", "error");
      }
    }
  };

  const handleRowClick = (shipment, e) => {
    if (
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('button') ||
      e.target.closest('a')
    ) {
      return;
    }
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      showToast("Session expired. Please log in again", "error");
      navigate("/login");
      return;
    }
    const ref = encodeURIComponent(shipment.order_reference_number);
    const url = `${window.location.origin}/shipment-details/${ref}?page=${currentPage}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.orderReferenceNumber) count++;
    if (filters.awbNumber) count++;
    if (filters.paymentMode) count++;
    if (filters.customerPhone) count++;
    if (filters.itemName) count++;
    if (filters.sku) count++;
    if (filters.courierPartnerName) count++;
    if (filters.orderStatus) count++;
    return count;
  }, [filters]);

  return (
    <div className={`relative min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 ${showMobileActions ? 'pb-24 md:pb-8' : 'pb-8'} overflow-x-hidden`}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
      />

      <div className="w-full max-w-full px-2 sm:px-3 lg:px-2 py-2 overflow-x-hidden">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200 w-full max-w-full">

          <div className="sticky top-0 z-20 bg-gradient-to-r from-slate-100 to-gray-100 border-b border-gray-300 shadow-sm">
            <div className="md:hidden p-1">
              <div className="grid grid-cols-2 gap-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabClick(tab.key)}
                    className={
                      `relative flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-medium transition-all duration-200 ` +
                      (selectedTab === tab.key
                        ? "bg-white shadow-md text-gray-900 ring-1 ring-slate-400/30"
                        : "text-gray-600 bg-white/30 active:bg-white/60")
                    }
                  >
                    <div className="flex items-center gap-1">
                      <tab.Icon
                        className={
                          `w-3.5 h-3.5 transition-colors flex-shrink-0 ` +
                          (selectedTab === tab.key ? tab.color : "text-gray-400")
                        }
                      />
                      <span className="text-[10px] font-semibold truncate max-w-[90px]">{tab.label}</span>
                    </div>
                    <span className={`${tab.badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shadow-sm`}>
                      {statusCounts[tab.key] ?? 0}
                    </span>
                    {selectedTab === tab.key && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-slate-500 to-slate-600 rounded-t-full"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden md:block relative">
              <div className="overflow-x-auto scrollbar-hide scroll-smooth">
                <div className="flex p-1 gap-1 min-w-min">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabClick(tab.key)}
                      className={
                        `group relative flex items-center gap-1 px-2 py-1 rounded-md font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ` +
                        (selectedTab === tab.key
                          ? "bg-white shadow-md text-gray-900 ring-1 ring-slate-400/30"
                          : "text-gray-600 hover:bg-white/50 hover:text-gray-900")
                      }
                    >
                      <tab.Icon
                        className={
                          `w-3.5 h-3.5 transition-colors flex-shrink-0 ` +
                          (selectedTab === tab.key ? tab.color : "text-gray-400 group-hover:text-gray-600")
                        }
                      />
                      <span className="text-xs font-semibold">{tab.label}</span>
                      <span className={`${tab.badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shadow-sm`}>
                        {statusCounts[tab.key] ?? 0}
                      </span>
                      {selectedTab === tab.key && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 h-0.5 bg-gradient-to-r from-slate-500 to-slate-600 rounded-t-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="top-[73px] z-20 border-b border-gray-300 bg-white">
            <div className="p-1.5 md:p-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all font-medium text-slate-700"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="font-semibold text-sm">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-slate-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
                <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showFilters ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="w-full sm:w-auto flex items-center gap-1">
                <select
                  value={analyticsDateRange}
                  onChange={(e) => setAnalyticsDateRange(e.target.value)}
                  className="px-2 py-1 text-xs border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm cursor-pointer hover:border-blue-400 font-medium text-gray-700"
                  title="Select date range for tab badge counts"
                >
                  <option value="thisMonth">This Month</option>
                  <option value="last30days">Last 30 Days</option>
                </select>

                <button
                  onClick={handleOpenCSVModal}
                  disabled={pageSelectedCount === 0}
                  className={`px-2 py-1 rounded-lg text-white font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1 ${pageSelectedCount === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 hover:shadow-lg'
                    }`}
                  title={pageSelectedCount === 0 ? "Select at least one row" : "Export selected orders as CSV"}
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden lg:inline">CSV</span>
                  {pageSelectedCount > 0 && (
                    <span className="bg-white text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pageSelectedCount}</span>
                  )}
                </button>

                <button
                  onClick={handleDownloadSelectedLabels}
                  disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                  className={`px-2 py-1 rounded-lg text-white font-semibold text-xs transition-all shadow-md flex items-center justify-center gap-1 ${pageSelectedCount === 0 || loadingDownloadSelected
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 hover:shadow-lg'
                    }`}
                  aria-busy={loadingDownloadSelected}
                  title={pageSelectedCount === 0 ? "Select at least one row" : "Download labels"}
                >
                  {loadingDownloadSelected ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      <span className="hidden lg:inline text-xs">Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      <span className="hidden lg:inline">Labels</span>
                      {pageSelectedCount > 0 && (
                        <span className="bg-white text-indigo-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pageSelectedCount}</span>
                      )}
                    </>
                  )}
                </button>

                <button
                  onClick={() => setViewMode(viewMode === 'comfortable' ? 'compact' : 'comfortable')}
                  className="flex-1 sm:flex-none px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all text-slate-700 text-xs font-medium flex items-center justify-center gap-1"
                  title={`Switch to ${viewMode === 'comfortable' ? 'compact' : 'comfortable'} view`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {viewMode === 'comfortable' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    )}
                  </svg>
                  <span className="hidden sm:inline text-xs">{viewMode === 'comfortable' ? 'Compact' : 'Comfortable'}</span>
                </button>
              </div>
            </div>

            {showFilters && selectedTab === "unprocessed" && (
              <form
                className="p-2 md:p-3 bg-gradient-to-br from-pink-50 to-rose-50 border-t border-pink-200 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleUnprocessedFilterClick(); }}
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                  <div className="group w-full lg:flex-1 lg:min-w-[160px]">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Order Reference Number</span>
                    </label>
                    <input
                      type="text"
                      name="orderReferenceNumber"
                      value={unprocessedFilters.orderReferenceNumber}
                      onChange={handleUnprocessedInputChange}
                      placeholder="Enter order reference"
                      className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  <div className="group w-full lg:w-[130px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span>Pickup Pincode</span>
                    </label>
                    <input
                      type="text"
                      name="pickupPincode"
                      value={unprocessedFilters.pickupPincode}
                      onChange={handleUnprocessedInputChange}
                      placeholder="e.g. 560001"
                      maxLength={6}
                      className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  {/* Drop Pincode */}
                  <div className="group w-full lg:w-[130px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span>Delivery Pincode</span>
                    </label>
                    <input
                      type="text"
                      name="dropPincode"
                      value={unprocessedFilters.dropPincode}
                      onChange={handleUnprocessedInputChange}
                      placeholder="e.g. 400001"
                      maxLength={6}
                      className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  {/* Date Range */}
                  <div className="w-full lg:w-auto lg:min-w-[220px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Date Range</span>
                    </label>
                    <div className="date-input-wrapper w-full [&_.MuiPaper-root]:!p-1 [&_.MuiPaper-root]:!pl-2.5 [&_.MuiPaper-root]:!min-h-0 [&_.MuiPaper-root]:!h-[30px] [&_.MuiTypography-root]:!text-xs [&_.MuiSvgIcon-root]:!text-[16px]">
                      <CustomDateRangePicker
                        selectedRange={[parseISO(unprocessedFilters.fromDate), parseISO(unprocessedFilters.toDate)]}
                        onRangeChange={([from, to]) => {
                          const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                          if (diffDays > 30) {
                            showToast("Date range cannot exceed 1 month", "error");
                            return;
                          }
                          setUnprocessedFilters(prev => ({
                            ...prev,
                            fromDate: from.toLocaleDateString("en-CA"),
                            toDate: to.toLocaleDateString("en-CA")
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="submit"
                      className="flex-1 lg:flex-none px-3 py-1.5 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-pink-700 hover:to-rose-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Apply Filters</span>
                    </button>
                    <button
                      type="button"
                      onClick={resetUnprocessedFilters}
                      className="flex-1 lg:flex-none px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Reset All</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {showFilters && selectedTab === "confirmation" && (
              <form
                className="p-2 md:p-3 bg-gradient-to-br from-cyan-50 to-sky-50 border-t border-cyan-200 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleConfirmationFilterClick(); }}
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                  <div className="group w-full lg:flex-1 lg:min-w-[160px]">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Order Reference</span>
                    </label>
                    <input type="text" name="orderReferenceNumber" value={confirmationFilters.orderReferenceNumber} onChange={handleConfirmationInputChange} placeholder="Enter order reference" className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white shadow-sm" />
                  </div>

                  <div className="group w-full lg:w-[150px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>Customer Phone</span>
                    </label>
                    <input type="text" name="customerPhone" value={confirmationFilters.customerPhone} onChange={handleConfirmationInputChange} placeholder="e.g. 9876543210" maxLength={10} className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white shadow-sm" />
                  </div>

                  <div className="group w-full lg:w-[150px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Status</span>
                    </label>
                    <select name="confirmationStatus" value={confirmationFilters.confirmationStatus} onChange={handleConfirmationInputChange} className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white shadow-sm cursor-pointer">
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="BYPASSED">Bypassed</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="EXPIRED">Expired</option>
                    </select>
                  </div>

                  <div className="w-full lg:w-auto lg:min-w-[220px] lg:flex-shrink-0">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Date Range</span>
                    </label>
                    <div className="date-input-wrapper w-full [&_.MuiPaper-root]:!p-1 [&_.MuiPaper-root]:!pl-2.5 [&_.MuiPaper-root]:!min-h-0 [&_.MuiPaper-root]:!h-[30px] [&_.MuiTypography-root]:!text-xs [&_.MuiSvgIcon-root]:!text-[16px]">
                      <CustomDateRangePicker
                        selectedRange={[parseISO(confirmationFilters.fromDate), parseISO(confirmationFilters.toDate)]}
                        onRangeChange={([from, to]) => {
                          const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                          if (diffDays > 30) {
                            showToast("Date range cannot exceed 1 month", "error");
                            return;
                          }
                          setConfirmationFilters(prev => ({
                            ...prev,
                            fromDate: from.toLocaleDateString("en-CA"),
                            toDate: to.toLocaleDateString("en-CA")
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-1.5 flex-shrink-0">
                    <button type="submit" className="flex-1 lg:flex-none px-3 py-1.5 bg-gradient-to-r from-cyan-600 to-sky-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-cyan-700 hover:to-sky-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Apply Filters</span>
                    </button>
                    <button type="button" onClick={resetConfirmationFilters} className="flex-1 lg:flex-none px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Reset All</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {showFilters && selectedTab !== "unprocessed" && selectedTab !== "confirmation" && (
              <form
                className="p-2 md:p-3 bg-gradient-to-br from-slate-50 to-gray-50 border-t border-gray-200 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleFilterClick(); }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-2.5 mb-3">
                  {[
                    {
                      name: "orderReferenceNumber",
                      label: "Order Reference Number",
                      placeholder: "Enter order reference number",
                      iconPath: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
                    },
                    {
                      name: "awbNumber",
                      label: "AWB Number",
                      placeholder: "Enter AWB / tracking number",
                      iconPath: "M4 6h16M4 10h16M4 14h16M4 18h16",
                    },
                    {
                      name: "customerPhone",
                      label: "Customer Phone",
                      placeholder: "Enter customer phone",
                      iconPath: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
                    },
                    {
                      name: "itemName",
                      label: "Item Name",
                      placeholder: "Search by product name",
                      iconPath: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
                    },
                    {
                      name: "sku",
                      label: "SKU",
                      placeholder: "Search by SKU code",
                      iconPath: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z",
                    },
                  ].map(({ name, label, placeholder, iconPath }) => (
                    <div key={name} className="group w-full">
                      <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                        </svg>
                        <span className="truncate">{label}</span>
                      </label>
                      <input
                        type="text"
                        name={name}
                        value={filters[name]}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        autoComplete="off"
                        className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm"
                      />
                    </div>
                  ))}

                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>Payment Mode</span>
                    </label>
                    <select
                      name="paymentMode"
                      value={filters.paymentMode}
                      onChange={handleInputChange}
                      className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm"
                    >
                      <option value="">All</option>
                      <option value="PREPAID">PREPAID</option>
                      <option value="COD">COD</option>
                    </select>
                  </div>

                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                      </svg>
                      <span>Courier Partner</span>
                    </label>
                    <select
                      name="courierPartnerName"
                      value={filters.courierPartnerName}
                      onChange={handleInputChange}
                      className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm"
                    >
                      <option value="">All Couriers</option>
                      {COURIER_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="group w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Order Status</span>
                    </label>
                    <StatusDropdown
                      value={filters.orderStatus}
                      onChange={(value) => setFilters((prev) => ({ ...prev, orderStatus: value }))}
                      statuses={Object.values(OrderBusinessStatusEnum)}
                    />
                  </div>

                  <div className="sm:col-span-1 w-full">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Created Date</span>
                      </label>
                      {useDateFilter && (
                        <button
                          type="button"
                          onClick={() => setUseDateFilter(false)}
                          className="text-[10px] text-red-600 hover:text-red-700 font-medium underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {useDateFilter ? (
                      <div className="date-input-wrapper w-full">
                        <CustomDateRangePicker
                          selectedRange={[parseISO(filters.fromDate), parseISO(filters.toDate)]}
                          onRangeChange={([from, to]) => {
                            const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                            if (diffDays > 30) {
                              showToast("Date range cannot exceed 1 month", "error");
                              return;
                            }
                            setFilters(prev => ({
                              ...prev,
                              fromDate: from.toLocaleDateString("en-CA"),
                              toDate: to.toLocaleDateString("en-CA")
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setUseDateFilter(true)}
                        className="w-full h-8 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-600 hover:border-blue-400 hover:bg-blue-50/50 transition-all font-medium"
                      >
                        + Add Created Date Range
                      </button>
                    )}
                  </div>

                  <div className="sm:col-span-1 w-full">
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M3 12l4-4m-4 4l4 4M21 12l-4-4m4 4l-4 4" />
                      </svg>
                      <span>Order Type</span>
                    </label>
                    <select
                      name="orderType"
                      value={filters.orderType}
                      onChange={handleInputChange}
                      disabled={selectedTab === "rvp"}
                      className={`w-full px-2.5 py-1.5 text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm ${
                        selectedTab === "rvp" ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                    >
                      <option value="">No Selection</option>
                      <option value="FORWARD">Forward</option>
                      <option value="REVERSE">Reverse</option>
                    </select>
                    
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap gap-1.5 items-stretch sm:items-center">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-3 py-1.5 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-lg shadow-md hover:shadow-lg hover:from-slate-700 hover:to-slate-800 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Apply Filters</span>
                  </button>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="w-full sm:w-auto px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Reset All</span>
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Mobile sticky action bar */}
          {showMobileActions && (
            <div className="fixed md:hidden bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t-2 border-gray-200 shadow-2xl px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Selected:</span>
                  <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">{pageSelectedCount}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadSelectedLabels}
                    disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                    className={`px-4 h-12 rounded-xl text-white text-sm font-semibold transition-all shadow-lg ${pageSelectedCount === 0 || loadingDownloadSelected
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800'
                      }`}
                    aria-busy={loadingDownloadSelected}
                  >
                    {loadingDownloadSelected ? "Processing…" : "Download"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== Error message (if any) ===== */}
          {error && (
            <div className="m-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* ===== Error / Unprocessed Orders Table ===== */}
          {selectedTab === "unprocessed" && (
            <div className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
              {loading ? (
                <TableSkeleton />
              ) : shipments.length === 0 ? (
                <EmptyState tab={selectedTab} onReset={resetUnprocessedFilters} />
              ) : (
                <table className="w-full table-auto">
                  <thead className="bg-gradient-to-r from-pink-600 to-rose-700 text-white sticky top-0 z-10">
                    <tr>
                      {['Order Reference', 'Pickup', 'Delivery', 'Invoice', 'Items', 'Error Summary', 'Actions'].map((h, i) => (
                        <th key={i} className={`${viewMode === 'compact' ? 'p-1.5 text-xs' : 'p-3'} text-left font-semibold tracking-wide ${h === 'Actions' ? 'sticky right-0 bg-rose-700 min-w-[140px]' : ''}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shipments.map((s, i) => {
                      const logs = s.carrier_allocation_attempt_logs || [];
                      const allFailed = logs.every(l => !l.is_successful_allocation);
                      const attemptCount = logs.length;
                      const isExpanded = expandedAllocLogs[i];
                      const isRetrying = retryingOrderIds.has(s.order_reference_number);

                      // Get the most recent failed attempt for error summary
                      const lastFailedLog = [...logs].reverse().find(l => !l.is_successful_allocation);
                      // Use carrierUtils to format names and messages
                      const lastCarrierName = lastFailedLog ? formatCarrierName(lastFailedLog.attempted_carrier_partner_public_name || lastFailedLog.attempted_carrier_partner_id) : '';
                      const lastFormattedError = lastFailedLog ? formatErrorMessage(lastFailedLog.error_message, lastFailedLog.error_code) : '';
                      const lastErrorCategory = lastFailedLog ? getErrorCategory(lastFailedLog.error_code, lastFailedLog.error_message) : '';

                      return (
                        <React.Fragment key={i}>
                          <tr className={`transition-all hover:bg-pink-50/50 ${viewMode === 'compact' ? 'text-sm' : ''} ${allFailed && attemptCount > 0 ? 'border-l-4 border-l-red-400' : ''}`}>
                            {/* Order Reference + Date */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                              <div className="flex flex-col space-y-0.5">
                                <span className="font-semibold text-gray-900 text-xs">{s.order_reference_number}</span>
                                <span className="text-[10px] text-gray-400">{s.updated_date_time ? formatInIST(s.updated_date_time) : '—'}</span>
                                <span className="text-[10px] text-gray-400 font-mono truncate max-w-[140px]" title={s.shipment_order_id}>{s.shipment_order_id}</span>
                              </div>
                            </td>

                            {/* Pickup */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                              {s.pickup_details ? (
                                <div className="flex flex-col space-y-0.5">
                                  <span className="text-xs font-medium text-gray-900">{s.pickup_details.pickup_poc || '—'}</span>
                                  <span className="text-[10px] text-gray-500">{[s.pickup_details.pickup_city, s.pickup_details.pickup_postal_code].filter(Boolean).join(', ')}</span>
                                </div>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>

                            {/* Delivery */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                              {s.drop_details ? (
                                <div className="flex flex-col space-y-0.5">
                                  <span className="text-xs font-medium text-gray-900">{s.drop_details.drop_poc || '—'}</span>
                                  <span className="text-[10px] text-gray-500">{[s.drop_details.drop_city, s.drop_details.drop_postal_code].filter(Boolean).join(', ')}</span>
                                </div>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </td>

                            {/* Invoice */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                              <span className="text-xs font-semibold text-gray-800">
                                {s.invoice_value ? `₹${parseFloat(s.invoice_value).toLocaleString('en-IN')}` : '—'}
                              </span>
                            </td>

                            {/* Items */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                              <div className="flex flex-col">
                                {(s.item_names || []).length > 0 ? (
                                  s.item_names.slice(0, 2).map((name, idx) => (
                                    <span key={idx} className="text-xs text-gray-700 truncate max-w-[150px]">{name}</span>
                                  ))
                                ) : <span className="text-xs text-gray-400">No items</span>}
                                {(s.item_names || []).length > 2 && (
                                  <span className="text-[10px] text-gray-500">+{s.item_names.length - 2} more</span>
                                )}
                              </div>
                            </td>

                            {/* Error Summary — using carrierUtils */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} max-w-[280px]`}>
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    allFailed ? 'bg-red-100 text-red-800 ring-1 ring-red-200' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'
                                  }`}>
                                    {attemptCount} attempt{attemptCount !== 1 ? 's' : ''} — {allFailed ? 'All Failed' : 'Partial'}
                                  </span>
                                  {lastErrorCategory && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{lastErrorCategory}</span>
                                  )}
                                  {attemptCount > 0 && (
                                    <button onClick={() => setExpandedAllocLogs(prev => ({ ...prev, [i]: !prev[i] }))} className="text-pink-600 hover:text-pink-800 transition-colors" title="View full error details">
                                      <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                  )}
                                </div>
                                {lastFailedLog && (
                                  <div className="flex flex-col space-y-0.5">
                                    <span className="text-[10px] font-semibold text-gray-600">{lastCarrierName}</span>
                                    <span className="text-[10px] text-rose-600 leading-snug">{lastFormattedError}</span>
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Actions */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} sticky right-0 bg-white border-l border-gray-100`}>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleRetryOrder(s)}
                                  disabled={isRetrying}
                                  className={`px-2 py-1 text-xs font-medium rounded-md border transition-all flex items-center gap-1 ${isRetrying ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed' : 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'}`}
                                  title="Retry — fetch order data and open in create order form"
                                >
                                  {isRetrying ? (
                                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                  )}
                                  {isRetrying ? 'Loading...' : 'Retry'}
                                </button>
                                <button
                                  onClick={() => { window.open(`/shipment-details/${s.order_reference_number}`, "_blank"); }}
                                  className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
                                  title="View Details"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Error Details */}
                          {isExpanded && attemptCount > 0 && (
                            <tr className="bg-pink-50/60">
                              <td colSpan={7} className="p-0">
                                <div className="px-4 py-3 space-y-3">
                                  {/* Error Diagnosis Card */}
                                  {lastFailedLog && (
                                    <div className="bg-white rounded-lg border-2 border-rose-200 p-3 shadow-sm">
                                      <h4 className="text-xs font-bold text-rose-700 mb-2 flex items-center gap-1.5">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                        Most Recent Error
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                        <div>
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Courier</span>
                                          <p className="text-xs font-medium text-gray-900 mt-0.5">{lastCarrierName}</p>
                                        </div>
                                        <div>
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Error Code</span>
                                          <div className="flex items-center gap-1.5 mt-0.5">
                                            <span className="text-xs font-mono font-semibold text-rose-700">{lastFailedLog.error_code || '—'}</span>
                                            {lastErrorCategory && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{lastErrorCategory}</span>}
                                          </div>
                                        </div>
                                        <div className="sm:col-span-2">
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Error Message</span>
                                          <p className="text-xs text-gray-800 mt-0.5 leading-relaxed">{lastFormattedError}</p>
                                          {/* Show raw message toggle if it was an OMSException */}
                                          {lastFailedLog.error_message && lastFailedLog.error_message.includes('OMSException') && (
                                            <details className="mt-1.5">
                                              <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">Show raw error</summary>
                                              <pre className="mt-1 text-[9px] text-gray-500 bg-gray-50 p-2 rounded border border-gray-200 max-h-24 overflow-auto whitespace-pre-wrap break-all font-mono">{lastFailedLog.error_message}</pre>
                                            </details>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* All Allocation Attempts Table */}
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                                      <svg className="w-3.5 h-3.5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                      All Carrier Allocation Attempts ({attemptCount})
                                    </h4>
                                    <div className="overflow-hidden rounded-lg border border-pink-200">
                                      <table className="w-full text-xs">
                                        <thead className="bg-pink-100 text-gray-700">
                                          <tr>
                                            <th className="px-3 py-1.5 text-left font-semibold">#</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Courier Name</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Status</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Category</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Error</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Timestamp</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-pink-100">
                                          {logs.map((log, logIdx) => (
                                            <tr key={logIdx} className={`hover:bg-pink-50 ${!log.is_successful_allocation ? 'bg-red-50/30' : ''}`}>
                                              <td className="px-3 py-1.5 text-gray-500">{log.attempted_carrier_partner_priority_order || logIdx + 1}</td>
                                              <td className="px-3 py-1.5 font-medium text-gray-800">{formatCarrierName(log.attempted_carrier_partner_public_name || log.attempted_carrier_partner_id)}</td>
                                              <td className="px-3 py-1.5">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${log.is_successful_allocation ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                  {log.is_successful_allocation ? 'Success' : 'Failed'}
                                                </span>
                                              </td>
                                              <td className="px-3 py-1.5">
                                                {!log.is_successful_allocation && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{getErrorCategory(log.error_code, log.error_message)}</span>}
                                              </td>
                                              <td className="px-3 py-1.5 text-gray-600 max-w-[300px]">
                                                <span className="block text-xs leading-snug" title={log.error_message}>{formatErrorMessage(log.error_message, log.error_code)}</span>
                                              </td>
                                              <td className="px-3 py-1.5 text-gray-500">{log.created_date_time_utc ? formatInIST(log.created_date_time_utc) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ===== Confirmation Orders Table ===== */}
          {selectedTab === "confirmation" && (
            <div className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
              {loading ? (
                <TableSkeleton />
              ) : shipments.length === 0 ? (
                <EmptyState tab={selectedTab} onReset={resetConfirmationFilters} />
              ) : (
                <table className="w-full table-auto">
                  <thead className="bg-gradient-to-r from-cyan-600 to-sky-700 text-white sticky top-0 z-10">
                    <tr>
                      {['Order Reference', 'Pickup', 'Delivery', 'Items', 'Amount', 'Status', 'Actions'].map((h, i) => (
                        <th key={i} className={`${viewMode === 'compact' ? 'p-1.5 text-xs' : 'p-3'} text-left font-semibold tracking-wide ${h === 'Actions' ? 'sticky right-0 bg-sky-700 min-w-[160px]' : ''}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shipments.map((s, i) => {
                      const items = (s.shipment_packages || []).flatMap(p => p.items || []);
                      const canPlaceOrder = s.confirmation_status === "PENDING" || s.confirmation_status === "CONFIRMED";
                      const statusColors = {
                        PENDING: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
                        CONFIRMED: 'bg-green-100 text-green-800 ring-1 ring-green-200',
                        BYPASSED: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
                        CANCELLED: 'bg-red-100 text-red-800 ring-1 ring-red-200',
                        EXPIRED: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200',
                      };

                      return (
                        <tr key={i} className={`transition-all hover:bg-cyan-50/50 ${viewMode === 'compact' ? 'text-sm' : ''} ${canPlaceOrder ? 'border-l-4 border-l-amber-400' : ''}`}>
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-0.5">
                              <span className="font-semibold text-gray-900 text-xs">{s.order_reference_number}</span>
                              <span className="text-[10px] text-gray-400">{s.order_created_date_time || '—'}</span>
                              <span className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded-full text-[9px] font-bold ${s.order_type === 'COD' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                {s.order_type || '—'}
                              </span>
                            </div>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-0.5">
                              <span className="text-xs font-medium text-gray-900">{s.pickup_name || '—'}</span>
                              <span className="text-[10px] text-gray-500">{[s.pickup_city, s.pickup_pincode].filter(Boolean).join(', ')}</span>
                            </div>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-0.5">
                              <span className="text-xs font-medium text-gray-900">{s.drop_name || '—'}</span>
                              <span className="text-[10px] text-gray-500">{[s.drop_city, s.drop_pincode].filter(Boolean).join(', ')}</span>
                              <span className="text-[10px] text-gray-400">{s.drop_poc_contact_number || ''}</span>
                            </div>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col">
                              {items.length > 0 ? items.slice(0, 2).map((item, idx) => (
                                <span key={idx} className="text-xs text-gray-700 truncate max-w-[150px]">
                                  {item.item_general_details?.item_name || '—'} {item.quantity > 1 ? `×${item.quantity}` : ''}
                                </span>
                              )) : <span className="text-xs text-gray-400">No items</span>}
                              {items.length > 2 && <span className="text-[10px] text-gray-500">+{items.length - 2} more</span>}
                            </div>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <span className="text-xs font-semibold text-gray-800">
                              {s.payment_details?.total_amount ? `₹${parseFloat(s.payment_details.total_amount).toLocaleString('en-IN')}` : '—'}
                            </span>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-1">
                              <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[s.confirmation_status] || 'bg-gray-100 text-gray-600'}`}>
                                {s.confirmation_status || '—'}
                              </span>
                              {s.confirmation_message_sent && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-green-600">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  WhatsApp sent
                                </span>
                              )}
                            </div>
                          </td>

                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} sticky right-0 bg-white border-l border-gray-100`}>
                            <div className="flex items-center gap-1">
                              {canPlaceOrder && (
                                <button
                                  onClick={() => {
                                    setConfirmModal({
                                      open: true,
                                      title: "Place Order",
                                      message: `Are you sure you want to place order ${s.order_reference_number}? This will bypass the confirmation step and initiate shipment processing.`,
                                      onConfirm: () => handleBypassConfirmation(s),
                                    });
                                  }}
                                  className="px-2 py-1 text-xs font-medium rounded-md border transition-all flex items-center gap-1 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border-cyan-200"
                                  title="Place order now"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  Place Order
                                </button>
                              )}
                              <button
                                onClick={() => { window.open(`/shipment-details/${s.order_reference_number}`, "_blank"); }}
                                className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
                                title="View Details"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ===== Shipments table ===== */}
          {selectedTab !== "unprocessed" && selectedTab !== "confirmation" && (
          <div className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
            {loading ? (
              <TableSkeleton />
            ) : shipments.length === 0 ? (
              <EmptyState tab={selectedTab} onReset={resetFilters} />
            ) : (
              <table className="w-full table-auto">
                <thead className="bg-gradient-to-r from-slate-600 to-slate-700 text-white sticky top-0 z-10">
                  <tr>
                    {/* Select-all checkbox header */}
                    <th className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} w-10`}>
                      <div className="flex items-center justify-center">
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                          className="h-5 w-5 accent-slate-600 cursor-pointer rounded border-2 border-white"
                          title={allOnPageSelected ? "Unselect all" : "Select all"}
                        />
                      </div>
                    </th>
                    {[
                      'Order Details', 'AWB / Tracking', 'Pickup', 'Delivery', 'Payment', 'Package', 'Items', 'Status', 'Actions'
                    ].map((h, i) => {
                      const isActions = h === 'Actions';
                      return (
                        <th
                          key={i}
                          className={`${viewMode === 'compact' ? 'p-1.5 text-xs' : 'p-3'} text-left font-semibold tracking-wide ${isActions ? 'sticky right-0 bg-slate-700 min-w-[120px]' : ''
                            }`}
                        >
                          {h}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {shipments.map((s, i) => {
                    const k = rowKey(s);
                    const checked = selectedRows.has(k);
                    const hasChannelMeta = Boolean(s.marketplace_channel_name || s.marketplace_channel_order_number);
                    const channelOrderDisplay =
                      s.marketplace_channel_name
                        ? (s.marketplace_channel_order_number ?? "—")
                        : s.marketplace_channel_order_number;

                    return (
                      <React.Fragment key={i}>
                        <tr
                          className={`relative transition-all ${checked ? 'bg-slate-50' : 'hover:bg-slate-50/50'
                            } ${viewMode === 'compact' ? 'text-sm' : ''}`}
                        >
                          {/* Row checkbox */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} w-10`}>
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSelectOne(k)}
                                className="h-5 w-5 accent-blue-600 cursor-pointer rounded"
                                title={checked ? "Unselect" : "Select"}
                              />
                            </div>
                          </td>

                          {/* Order Details */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-1">
                              <span className="font-semibold text-gray-900">{s.order_reference_number}</span>
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {formatInIST(s.order_placed_date_time)}
                              </span>

                              {hasChannelMeta && (
                                <span className="inline-flex max-w-fit items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                  </svg>
                                  {s.marketplace_channel_name && <span className="truncate">{s.marketplace_channel_name}</span>}
                                  {s.marketplace_channel_name && channelOrderDisplay && <span className="opacity-60">•</span>}
                                  {channelOrderDisplay && <span className="truncate">{channelOrderDisplay}</span>}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* AWB / Tracking */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-1">
                              {s.awb_number ? (
                                <span className="flex items-center gap-1">
                                  <span
                                    onMouseDown={handleAwbMouseDown}
                                    onMouseUp={(e) => handleAwbMouseUp(e, s.awb_number)}
                                    className="text-blue-600 hover:text-blue-800 underline font-semibold cursor-pointer select-text inline-flex items-center gap-1"
                                    title="Click to open live tracking"
                                  >
                                    <svg className="w-4 h-4 flex-shrink-0 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {s.awb_number}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(s.awb_number)}
                                    className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                    title="Copy AWB number"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs text-gray-600 font-medium">
                                  {s.allocated_carrier_partner_public_name || 'N/A'}
                                </span>
                                {s.shipment_order_type && (
                                  <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-normal text-gray-500 ${
                                    s.shipment_order_type === 'RVP' 
                                      ? 'bg-purple-50' 
                                      : 'bg-blue-50'
                                  }`}>
                                    {s.shipment_order_type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Pickup */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} relative group`}>
                            <div className="flex flex-col space-y-1 text-xs text-gray-700">
                              <span className="font-medium truncate">{s.pickup_organisation}</span>
                              <span className="text-gray-500 truncate">
                                {s.pickup_city} - {s.pickup_pincode}
                              </span>
                            </div>
                            {/* Tooltip on hover */}
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl">
                              <div className="space-y-1">
                                <div className="font-semibold">{s.pickup_organisation}</div>
                                <div>{s.pickup_address_line_1}</div>
                                {s.pickup_address_line_2 && <div>{s.pickup_address_line_2}</div>}
                                <div>{s.pickup_city}, {s.pickup_state} - {s.pickup_pincode}</div>
                                {s.pickup_phone && <div>Phone: {s.pickup_phone}</div>}
                              </div>
                              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                          </td>

                          {/* Delivery */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} max-w-[140px] relative group`}>
                            <div className="flex flex-col space-y-1 text-xs text-gray-700">
                              <span className="font-medium truncate">{s.drop_name}</span>
                              <span className="text-gray-500 truncate">
                                {s.drop_city} - {s.drop_pincode}
                              </span>
                            </div>
                            {/* Tooltip on hover */}
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl">
                              <div className="space-y-1">
                                <div className="font-semibold">{s.drop_name}</div>
                                <div>{s.drop_address_line_1}</div>
                                {s.drop_address_line_2 && <div>{s.drop_address_line_2}</div>}
                                <div>{s.drop_city}, {s.drop_state} - {s.drop_pincode}</div>
                                {s.drop_phone && <div>Phone: {s.drop_phone}</div>}
                              </div>
                              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                          </td>

                          {/* Payment */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-1">
                              <span className="font-bold text-gray-900">₹{Number(s.payment_details?.total_amount ?? 0).toFixed(2)}</span>
                              <span className={`inline-flex max-w-fit items-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.payment_details?.payment_mode === 'COD'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-green-100 text-green-800'
                                }`}>
                                {s.payment_details?.payment_mode}
                              </span>
                            </div>
                          </td>

                          {/* Package */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <div className="flex flex-col space-y-1 text-xs">
                              <div className="flex items-center gap-1 text-gray-700">
                                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <span className="font-medium">{(Number(s.dead_weight_in_grams ?? 0) / 1000).toFixed(2)} kg</span>
                              </div>
                              <div className="text-gray-500">
                                Vol: {(Number(s.volumetric_weight_in_grams ?? 0) / 1000).toFixed(2)} kg
                              </div>
                              {s.charged_weight_category && (
                                <span className="text-xs text-blue-600 font-medium">
                                  {s.charged_weight_category.replace(/_/g, " ")}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Items */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <button
                              onClick={() => toggleRow(i)}
                              className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-semibold transition-all flex items-center gap-1"
                            >
                              <svg className={`w-4 h-4 transition-transform ${expandedRows[i] ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              {expandedRows[i] ? 'Hide' : 'View'}
                            </button>
                          </td>

                          {/* Status */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'}`}>
                            <StatusBadge 
                              statusCode={s.latest_order_status_code} 
                              shipment={s}
                              onSupportClick={handleOpenSupport}
                            />
                          </td>

                          {/* Actions */}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} bg-white sticky right-0`}>
                            <ActionMenu
                              shipment={s}
                              onAction={handleActionClick}
                              currentPage={currentPage}
                              onOpenChange={(isOpen) => setOpenActionMenuRow(isOpen ? i : null)}
                            />
                          </td>
                        </tr>

                        {/* Expanded Row - Items Table */}
                        {expandedRows[i] && (
                          <tr>
                            <td colSpan={10} className="p-0 bg-gradient-to-br from-gray-50 to-blue-50/30">
                              <div className="p-6 animate-slide-down">
                                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                  Order Items
                                </h4>
                                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-100 border-b border-gray-200">
                                      <tr>
                                        <th className="p-3 text-left font-semibold text-gray-700">SKU</th>
                                        <th className="p-3 text-left font-semibold text-gray-700">Item Name</th>
                                        <th className="p-3 text-center font-semibold text-gray-700">Quantity</th>
                                        <th className="p-3 text-right font-semibold text-gray-700">Unit Price</th>
                                        <th className="p-3 text-right font-semibold text-gray-700">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {(s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []).map((item, j) => (
                                        <tr key={j} className="hover:bg-gray-50">
                                          <td className="p-3 font-mono text-xs text-gray-600">{item.item_general_details.sku}</td>
                                          <td className="p-3 font-medium text-gray-900">{item.item_general_details.item_name}</td>
                                          <td className="p-3 text-center">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                              {item.quantity}
                                            </span>
                                          </td>
                                          <td className="p-3 text-right font-semibold text-gray-700">₹{item.item_payment_details.unit_price}</td>
                                          <td className="p-3 text-right font-bold text-gray-900">₹{(item.quantity * item.item_payment_details.unit_price).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* ===== Pagination ===== */}
          {!loading && shipments.length > 0 && (
            <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100/50 p-2 md:p-3">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center gap-1.5 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>

                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-600">Page</span>
                    <span className="font-bold text-blue-600 text-lg">{currentPage}</span>
                    {totalPages > 1 && (
                      <>
                        <span className="text-gray-400">of</span>
                        <span className="font-bold text-gray-700">{totalPages}</span>
                      </>
                    )}
                  </div>

                  <button
                    onClick={nextPage}
                    disabled={shipments.length < limit}
                    className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center gap-1.5 text-sm"
                  >
                    Next
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Rows:</span>
                  <select
                    value={limit}
                    onChange={handleLimitChange}
                    className="px-2 py-1.5 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm cursor-pointer text-sm"
                  >
                    {[50, 100, 200].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Claim Upload Modal ===== */}
      <ClaimUploadModal
        open={claimModalOpen}
        onClose={(result) => {
          setClaimModalOpen(false);
          setClaimShipment(null);
          setClaimDefaultItem(null);
          if (result?.success) {
            fetchData(selectedTab, { skip: (currentPage - 1) * limit });
            showToast("Claim submitted successfully", "success");
          }
        }}
        shipment={claimShipment}
        defaultItem={claimDefaultItem}
      />

      {/* ===== Support Ticket Modal ===== */}
      <CreateTicketModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
        }}
        onSuccess={(ticket) => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
          navigate(`/support/tickets/${ticket.public_id}`);
        }}
        orderContext={supportOrderContext}
      />

      {/* CSV Export Modal */}
      {showCSVColumnModal && (
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-slate-900/90 backdrop-blur-lg p-0 sm:p-2 md:p-4 lg:p-6 animate-fade-in overflow-hidden"
          onClick={() => setShowCSVColumnModal(false)}
        >
          <div 
            className="bg-white rounded-none sm:rounded-2xl md:rounded-3xl shadow-2xl w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[96vh] lg:max-h-[95vh] overflow-hidden flex flex-col animate-scale-in border-0 sm:border-2 border-slate-200/80 shadow-slate-900/30 max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 lg:py-6 flex items-center justify-between border-b-0 relative overflow-hidden w-full min-w-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-transparent to-indigo-600/20"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/10 rounded-full -ml-24 -mb-24 blur-2xl"></div>
              
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 flex-1 min-w-0 pr-1 sm:pr-2 relative z-10">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 rounded-lg sm:rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-xl shadow-blue-900/30 flex-shrink-0">
                  <CSVIcon.Download className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 text-white" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-white mb-0.5 sm:mb-1 truncate drop-shadow-sm">Export Data to CSV</h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-blue-100 truncate">
                    {shipments.filter(isSelectedOnPage).length} {shipments.filter(isSelectedOnPage).length === 1 ? 'shipment' : 'shipments'} selected
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCSVColumnModal(false)}
                className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-11 lg:h-11 rounded-lg sm:rounded-xl md:rounded-2xl bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-sm border border-white/20 text-white hover:text-white flex items-center justify-center transition-all duration-200 flex-shrink-0 shadow-lg hover:shadow-xl relative z-10"
                aria-label="Close modal"
              >
                <CSVIcon.X className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 md:w-5 lg:h-6 lg:w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-b from-slate-50/50 via-white to-slate-50/30 min-w-0">
              <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 border-b border-slate-200/60 bg-gradient-to-b from-white to-slate-50/50 overflow-x-hidden min-w-0 w-full">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 md:gap-3 mb-3 sm:mb-4 w-full min-w-0">
                  <div className="flex-1 relative min-w-0 w-full">
                    <CSVIcon.Search className="absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400 pointer-events-none flex-shrink-0 z-10" />
                    <input
                      type="text"
                      placeholder="Search columns..."
                      value={csvColumnSearch}
                      onChange={(e) => setCsvColumnSearch(e.target.value)}
                      className="w-full max-w-full pl-9 sm:pl-10 md:pl-12 pr-2.5 sm:pr-3 md:pr-4 py-2 sm:py-2.5 md:py-3 rounded-xl sm:rounded-2xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-200 bg-white text-xs sm:text-sm md:text-base text-slate-900 placeholder:text-slate-400 shadow-sm hover:border-slate-300 hover:shadow-md font-medium min-w-0"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
                    <button
                      onClick={() => {
                        const allKeys = allCSVColumns.map(col => col.key);
                        setSelectedCSVColumns(allKeys);
                        setCsvColumnOrder(allKeys);
                      }}
                      className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 text-white font-bold transition-all duration-200 text-xs sm:text-sm flex items-center gap-1 sm:gap-1.5 border-0 shadow-lg hover:shadow-xl active:shadow-md whitespace-nowrap flex-shrink-0"
                    >
                      <CSVIcon.Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Select All</span>
                      <span className="sm:hidden">All</span>
                    </button>
                    <button
                      onClick={() => {
                        const defaultKeys = allCSVColumns.filter(col => col.defaultSelected).map(col => col.key);
                        setSelectedCSVColumns(defaultKeys);
                        setCsvColumnOrder(defaultKeys);
                      }}
                      className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg sm:rounded-xl md:rounded-2xl bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 hover:text-slate-900 font-bold transition-all duration-200 text-xs sm:text-sm border-2 border-slate-300 hover:border-slate-400 shadow-sm hover:shadow-md whitespace-nowrap flex-shrink-0"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 flex-wrap w-full min-w-0">
                  {csvColumnCategories.map((cat) => {
                    const isActive = csvColumnCategory === cat.key;
                    const categoryColumns = cat.key === "all" 
                      ? allCSVColumns 
                      : allCSVColumns.filter(col => col.category === cat.key);
                    const selectedInCategory = categoryColumns.filter(col => selectedCSVColumns.includes(col.key)).length;
                    
                    const getCategoryClasses = (color, active) => {
                      const colorMap = {
                        slate: active 
                          ? 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-lg border-slate-600 ring-2 ring-slate-500/30'
                          : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300 hover:border-slate-400 shadow-sm hover:shadow-md',
                        indigo: active
                          ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg border-indigo-500 ring-2 ring-indigo-500/30'
                          : 'bg-white text-indigo-700 hover:bg-indigo-50 border-indigo-200 hover:border-indigo-300 shadow-sm hover:shadow-md',
                        blue: active
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg border-blue-500 ring-2 ring-blue-500/30'
                          : 'bg-white text-blue-700 hover:bg-blue-50 border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md',
                        emerald: active
                          ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg border-emerald-500 ring-2 ring-emerald-500/30'
                          : 'bg-white text-emerald-700 hover:bg-emerald-50 border-emerald-200 hover:border-emerald-300 shadow-sm hover:shadow-md',
                        amber: active
                          ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg border-amber-500 ring-2 ring-amber-500/30'
                          : 'bg-white text-amber-700 hover:bg-amber-50 border-amber-200 hover:border-amber-300 shadow-sm hover:shadow-md',
                        purple: active
                          ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg border-purple-500 ring-2 ring-purple-500/30'
                          : 'bg-white text-purple-700 hover:bg-purple-50 border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md',
                        rose: active
                          ? 'bg-gradient-to-r from-rose-600 to-rose-700 text-white shadow-lg border-rose-500 ring-2 ring-rose-500/30'
                          : 'bg-white text-rose-700 hover:bg-rose-50 border-rose-200 hover:border-rose-300 shadow-sm hover:shadow-md',
                      };
                      return colorMap[color] || colorMap.slate;
                    };

                    const getBadgeClasses = (color, active) => {
                      const colorMap = {
                        slate: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-slate-100 text-slate-700',
                        indigo: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-indigo-100 text-indigo-800',
                        blue: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-blue-100 text-blue-800',
                        emerald: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-emerald-100 text-emerald-800',
                        amber: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-amber-100 text-amber-800',
                        purple: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-purple-100 text-purple-800',
                        rose: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-rose-100 text-rose-800',
                      };
                      return colorMap[color] || colorMap.slate;
                    };
                    
                    return (
                      <button
                        key={cat.key}
                        onClick={() => setCsvColumnCategory(cat.key)}
                        className={`px-2 sm:px-2.5 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-[10px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 border-2 flex-shrink-0 max-w-full ${getCategoryClasses(cat.color, isActive)}`}
                      >
                        <span className="text-sm sm:text-base md:text-lg flex-shrink-0">{cat.icon}</span>
                        <span className="whitespace-nowrap text-xs sm:text-sm">{cat.label}</span>
                        <span className={`text-[9px] sm:text-[10px] md:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-extrabold flex-shrink-0 ${getBadgeClasses(cat.color, isActive)}`}>
                          {selectedInCategory}/{categoryColumns.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-3 md:p-4 lg:p-6 xl:p-8 custom-scrollbar min-w-0 w-full">
                {(() => {
                  const filteredColumns = allCSVColumns.filter(col => {
                    const matchesSearch = !csvColumnSearch || 
                      col.label.toLowerCase().includes(csvColumnSearch.toLowerCase()) ||
                      col.description?.toLowerCase().includes(csvColumnSearch.toLowerCase());
                    const matchesCategory = csvColumnCategory === "all" || col.category === csvColumnCategory;
                    return matchesSearch && matchesCategory;
                  });

                  const orderedColumns = csvColumnOrder.length > 0
                    ? [...csvColumnOrder.map(key => filteredColumns.find(col => col.key === key)).filter(Boolean),
                       ...filteredColumns.filter(col => !csvColumnOrder.includes(col.key))]
                    : filteredColumns;

                  if (orderedColumns.length === 0) {
                    return (
                      <div className="text-center py-12 sm:py-16">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                          <CSVIcon.Search className="h-8 w-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600 text-base sm:text-lg font-medium">No columns found</p>
                        <p className="text-slate-500 text-sm mt-1">Try adjusting your search or filter</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2 sm:space-y-2.5">
                      {orderedColumns.map((column, index) => {
                        const isSelected = selectedCSVColumns.includes(column.key);
                        const category = csvColumnCategories.find(cat => cat.key === column.category);
                        
                        return (
                          <div
                            key={column.key}
                            draggable
                            onDragStart={(e) => {
                              setDraggedColumn(column.key);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedColumn && draggedColumn !== column.key) {
                                const newOrder = [...csvColumnOrder];
                                const draggedIndex = newOrder.indexOf(draggedColumn);
                                const targetIndex = newOrder.indexOf(column.key);
                                if (draggedIndex !== -1 && targetIndex !== -1) {
                                  newOrder.splice(draggedIndex, 1);
                                  newOrder.splice(targetIndex, 0, draggedColumn);
                                  setCsvColumnOrder(newOrder);
                                }
                              }
                              setDraggedColumn(null);
                            }}
                            className={`group flex items-center gap-2 sm:gap-3 md:gap-4 p-2.5 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 cursor-move ${
                              isSelected
                                ? 'border-blue-500 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 hover:from-blue-50 hover:to-indigo-50 shadow-md shadow-blue-500/20 ring-2 ring-blue-500/10'
                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80 shadow-sm hover:shadow-md'
                            } ${draggedColumn === column.key ? 'opacity-40 scale-95' : ''}`}
                          >
                            <div className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors cursor-grab active:cursor-grabbing">
                              <CSVIcon.GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>

                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCSVColumns([...selectedCSVColumns, column.key]);
                                  if (!csvColumnOrder.includes(column.key)) {
                                    setCsvColumnOrder([...csvColumnOrder, column.key]);
                                  }
                                } else {
                                  setSelectedCSVColumns(selectedCSVColumns.filter(k => k !== column.key));
                                }
                              }}
                              className="h-4 w-4 sm:h-5 sm:w-5 accent-blue-600 cursor-pointer flex-shrink-0 rounded border-2 border-slate-300 focus:ring-2 focus:ring-blue-500/30 transition-all"
                            />

                            <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center text-base sm:text-lg md:text-xl shadow-sm ${
                              isSelected ? 'bg-gradient-to-br from-blue-100 to-indigo-100 border-2 border-blue-300' : 'bg-slate-100 border-2 border-slate-200'
                            }`}>
                              {column.icon}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                                <span className={`font-bold text-sm sm:text-base md:text-lg ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                                  {column.label}
                                </span>
                                {column.defaultSelected && (
                                  <span className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 font-bold border-2 border-blue-200">
                                    Default
                                  </span>
                                )}
                                {category && (() => {
                                  const categoryColorMap = {
                                    slate: 'bg-slate-100 text-slate-700 border-slate-200',
                                    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
                                    blue: 'bg-blue-100 text-blue-700 border-blue-200',
                                    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                                    amber: 'bg-amber-100 text-amber-700 border-amber-200',
                                    purple: 'bg-purple-100 text-purple-700 border-purple-200',
                                    rose: 'bg-rose-100 text-rose-700 border-rose-200',
                                  };
                                  return (
                                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-semibold border ${categoryColorMap[category.color] || categoryColorMap.slate}`}>
                                      {category.label}
                                    </span>
                                  );
                                })()}
                              </div>
                              {column.description && (
                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{column.description}</p>
                              )}
                            </div>

                            {isSelected && (
                              <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/40 ring-2 ring-blue-500/20">
                                <CSVIcon.Check className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 md:w-5 text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {selectedCSVColumns.length > 0 && (
                <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 border-t-2 border-slate-200/60 bg-gradient-to-b from-blue-50/30 via-white to-slate-50/30 w-full min-w-0 overflow-x-hidden">
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 mb-2 sm:mb-2.5 md:mb-3 flex-wrap min-w-0">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
                      <CSVIcon.Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 lg:h-4.5 lg:w-4.5 text-white" />
                    </div>
                    <span className="text-xs sm:text-sm md:text-base font-bold text-slate-900 whitespace-nowrap">Export Preview</span>
                    <span className="text-xs sm:text-sm md:text-base font-bold text-blue-700 bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0">({selectedCSVColumns.length} columns)</span>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 max-h-24 sm:max-h-28 overflow-y-auto custom-scrollbar w-full min-w-0">
                    {csvColumnOrder.length > 0
                      ? csvColumnOrder
                          .filter(key => selectedCSVColumns.includes(key))
                          .map(key => {
                            const col = allCSVColumns.find(c => c.key === key);
                            return col ? (
                              <span
                                key={key}
                                className="inline-flex items-center gap-0.5 sm:gap-1 md:gap-1.5 px-1.5 sm:px-2 md:px-2.5 lg:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 text-[10px] sm:text-xs md:text-sm text-blue-900 font-bold shadow-sm hover:border-blue-400 hover:shadow-md hover:from-blue-50 hover:to-indigo-50 transition-all flex-shrink-0 max-w-full"
                              >
                                <span className="text-[10px] sm:text-xs md:text-sm flex-shrink-0">{col.icon}</span>
                                <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-[150px] md:max-w-none">{col.label}</span>
                              </span>
                            ) : null;
                          })
                      : selectedCSVColumns.map(key => {
                          const col = allCSVColumns.find(c => c.key === key);
                          return col ? (
                            <span
                              key={key}
                              className="inline-flex items-center gap-0.5 sm:gap-1 md:gap-1.5 px-1.5 sm:px-2 md:px-2.5 lg:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 text-[10px] sm:text-xs md:text-sm text-blue-900 font-bold shadow-sm hover:border-blue-400 hover:shadow-md hover:from-blue-50 hover:to-indigo-50 transition-all flex-shrink-0 max-w-full"
                            >
                              <span className="text-[10px] sm:text-xs md:text-sm flex-shrink-0">{col.icon}</span>
                              <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-[150px] md:max-w-none">{col.label}</span>
                            </span>
                          ) : null;
                        })}
                  </div>
                </div>
              )}
            </div>

            <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 border-t-2 border-slate-200/60 bg-gradient-to-b from-white to-slate-50/50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 md:gap-4 w-full min-w-0 overflow-x-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 flex-1 min-w-0">
                <div className="text-xs sm:text-sm md:text-base text-slate-700 font-medium whitespace-nowrap flex-shrink-0">
                  <span className="font-extrabold text-blue-700 text-base sm:text-lg md:text-xl">{selectedCSVColumns.length}</span>
                  <span className="mx-1 sm:mx-1.5">of</span>
                  <span className="font-bold">{allCSVColumns.length}</span>
                  <span className="ml-1 sm:ml-1.5">columns selected</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
                  <label className="text-xs sm:text-sm text-slate-700 font-bold whitespace-nowrap flex-shrink-0">Format:</label>
                  <select
                    value={csvExportFormat}
                    onChange={(e) => setCsvExportFormat(e.target.value)}
                    className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border-2 border-slate-300 bg-white text-xs sm:text-sm text-slate-900 font-bold focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none shadow-sm hover:border-slate-400 hover:shadow-md transition-all min-w-0 flex-shrink-0"
                  >
                    <option value="csv">CSV (.csv)</option>
                    <option value="excel">Excel Compatible</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0 min-w-0">
                <button
                  onClick={() => setShowCSVColumnModal(false)}
                  className="px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-bold rounded-lg sm:rounded-xl md:rounded-2xl bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100 transition-all duration-200 shadow-md hover:shadow-lg flex-1 sm:flex-none whitespace-nowrap min-w-0"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (selectedCSVColumns.length === 0) {
                      showToast("Please select at least one column", "error");
                      return;
                    }
                    setDownloadingCSV(true);
                    try {
                      await new Promise(resolve => setTimeout(resolve, 300));
                      handleDownloadSelectedCSV();
                      setShowCSVColumnModal(false);
                      showToast(`CSV exported successfully with ${selectedCSVColumns.length} columns`, "success");
                    } catch (error) {
                      showToast("Failed to export CSV", "error");
                    } finally {
                      setDownloadingCSV(false);
                    }
                  }}
                  disabled={selectedCSVColumns.length === 0 || downloadingCSV}
                  className="px-4 sm:px-6 md:px-8 lg:px-10 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-extrabold rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white hover:from-blue-500 hover:via-blue-600 hover:to-indigo-600 disabled:from-slate-400 disabled:via-slate-500 disabled:to-slate-500 disabled:cursor-not-allowed shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/50 active:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none min-w-[100px] sm:min-w-[120px] md:min-w-[140px] ring-2 ring-blue-500/20 hover:ring-4 hover:ring-blue-500/30 whitespace-nowrap"
                >
                  {downloadingCSV ? (
                    <>
                      <span className="inline-block w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                      <span className="hidden sm:inline">Exporting...</span>
                      <span className="sm:hidden">Export...</span>
                    </>
                  ) : (
                    <>
                      <CSVIcon.Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                      <span>Export CSV</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }

        /* Hide scrollbar but keep functionality */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Professional Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #94a3b8 #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #cbd5e1, #94a3b8);
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #94a3b8, #64748b);
          border-color: #cbd5e1;
        }

        .custom-scrollbar::-webkit-scrollbar-corner {
          background: #f8fafc;
        }

        .date-input-wrapper > .MuiPaper-root {
          width: 100%;
          height: 32px;
          padding: 0 0.75rem;
          border: 2px solid #e5e7eb !important;
          border-radius: 0.5rem !important;
          background: #ffffff !important;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: all 0.2s;
        }

        .date-input-wrapper > .MuiPaper-root:hover {
          border-color: #64748b !important;
          box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.1) !important;
        }

        .date-input-wrapper .MuiTypography-root {
          font-size: 0.75rem;
          color: #111827;
          font-weight: 500;
        }

        .date-input-wrapper .MuiSvgIcon-root {
          font-size: 16px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}
